import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { FormationPicker } from '../components/formation/FormationPicker';
import { peutVoirCouts } from '../lib/roles';
import { AftPage } from '../components/aft/AftPage';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ArrowLeft, Save, Loader2, Plus, X, Calendar as CalendarIcon, Building2, Landmark, CalendarRange, UserPlus, Users, Trash2, ChevronDown, ChevronRight as ChevronRightIcon, BookOpen, Receipt, FileCheck, AlertTriangle, Check, CreditCard, FileText, Upload, Wand2, Briefcase, Key, Copy as CopyIcon, CornerDownRight, Eye, CalendarDays } from 'lucide-react';
import { ListeCandidatsDialog, PdfButton, ExcelButton } from './formateur/journee/JourneeDialogs';
import { StatusBadge } from '../components/common/StatusBadge';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { format, parseISO, max } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { Checkbox } from '../components/ui/checkbox';
import WorkflowPanel from '../components/WorkflowPanel';
import { PlanningPedagogiqueTab } from './dossier/PlanningPedagogiqueTab';
import { FinancementTab } from './dossier/FinancementTab';
import { FilChronologiqueTab } from './dossier/FilChronologiqueTab';
import { RapportDossierPanel } from './rapports/RapportDossierPanel';
import { FacturationTab } from './dossier/FacturationTab';
import { getAuthToken } from '../lib/authToken';
import { TYPES_FINANCEMENT, MODALITES_FACTURATION } from './dossier/constants';
import { isJourFerie, isJourFerieDate, getJourFerieLabel } from '../lib/joursFeries';
import { formatNom, formatPrenom, sortPersonnes } from '../lib/personne';
import { CiviliteSelect } from '../components/CiviliteSelect';
import { AbandonsDossierDialog } from '../components/candidat/AbandonsDossierDialog';
import { CandidatLink } from '../components/candidat/CandidatLink';
import { CandidatFicheActions } from '../components/candidat/CandidatFicheActions';
import { Kpi as SuiviKpi } from '../components/entreprise/CandidatsListe';
import { AftsManquantesInline } from '../components/aft/AftsManquantesInline';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HORAIRES_DEFAUT = { matin_debut: '08:00', matin_fin: '12:00', am_debut: '13:30', am_fin: '16:30' };
const toMin = (h) => { const [a, b] = String(h || '').split(':').map(Number); return Number.isFinite(a) && Number.isFinite(b) ? a * 60 + b : 0; };
const heuresHoraires = (h) => Math.round(((Math.max(0, toMin(h.matin_fin) - toMin(h.matin_debut)) + Math.max(0, toMin(h.am_fin) - toMin(h.am_debut))) / 60) * 100) / 100;
const horairesLabel = (h, periode) => periode === 'matin' ? `${h.matin_debut}–${h.matin_fin}` : periode === 'apres_midi' ? `${h.am_debut}–${h.am_fin}` : `${h.matin_debut}–${h.matin_fin} / ${h.am_debut}–${h.am_fin}`;

const TYPES_FORMATION = [
  { value: 'presentiel', label: 'Présentiel' },
  { value: 'distanciel', label: 'Distanciel' },
  { value: 'mixte', label: 'Mixte' },
  { value: 'elearning', label: 'E-learning' },
  { value: 'afest', label: 'AFEST' }
];

const TYPES_FINANCEUR = [
  { value: 'opco', label: 'OPCO' },
  { value: 'cpf', label: 'CPF' },
  { value: 'pole_emploi', label: 'France Travail' },
  { value: 'region', label: 'Région' },
  { value: 'entreprise', label: 'Entreprise' },
  { value: 'autre', label: 'Autre' },
];

export const DossierFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const voirCouts = peutVoirCouts(user);
  const isEdit = !!id;
  const headers = useMemo(() => ({ Authorization: `Bearer ${getAuthToken()}` }), []);

  const [formData, setFormData] = useState({
    intitule: '',
    client_id: '',
    client: '',
    type_formation: 'continue',
    financeur_id: '',
    financeur: '',
    date_debut: '',
    date_fin: '',
    formation_id: '',
    administrateur_id: user?.id || '',
    referent_id: '',
    lieu_principal: '',
    heure_debut: '08:00',
    horaires: { ...HORAIRES_DEFAUT },
    lieu_secondaire: '',
    code_action_client: '',
    candidats: [],
    planning: [],
    periodes_stage: [],
    plan_financement: null,
    bons_de_commande: [],
    statut_administratif: 'brouillon',
    pieces_justificatives: [],
    conditions_facturation: 'fin_session',
    af_liees: [],
    statut: 'en_cours'
  });

  const [formations, setFormations] = useState([]);
  const [candidats, setCandidats] = useState([]);
  const [allDossiers, setAllDossiers] = useState([]);
  const [formateurs, setFormateurs] = useState([]);
  const [modules, setModules] = useState([]);
  const [clients, setClients] = useState([]);
  const [financeurs, setFinanceurs] = useState([]);
  const [lieux, setLieux] = useState([]);
  const [deverrouilles, setDeverrouilles] = useState([]); // journées verrouillées ouvertes à la modification par l'admin (jusqu'à l'enregistrement)
  const [dossiersFinanceursSummary, setDossiersFinanceursSummary] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  // Dialog for creating new lieu
  const [lieuDialogOpen, setLieuDialogOpen] = useState(false);
  // Dialog for creating a new AF to attach
  const [afLieeDialogOpen, setAfLieeDialogOpen] = useState(false);
  const [newAfLiee, setNewAfLiee] = useState({ intitule: '', formation_id: '', date_debut: format(new Date(), 'yyyy-MM-dd') });
  const [creatingAfLiee, setCreatingAfLiee] = useState(false);
  const [expressAf, setExpressAf] = useState(null); // eslint-disable-line no-unused-vars

  const handleCreateAfLiee = async () => {
    if (!newAfLiee.intitule.trim() || !newAfLiee.formation_id || !newAfLiee.date_debut) {
      toast.error("Renseignez l'intitulé, la formation et la date de début");
      return;
    }
    setCreatingAfLiee(true);
    try {
      const resp = await axios.post(`${API_URL}/dossiers`, {
        intitule: newAfLiee.intitule.trim(),
        formation_id: newAfLiee.formation_id,
        date_debut: newAfLiee.date_debut,
        date_fin: newAfLiee.date_debut,
        planning: [{ date: newAfLiee.date_debut, demi_journee: 'journee', formateur_id: '', module_id: '', modules_pedagogiques: [], type_journee: 'formation', organisme_externe: '', lieu: '' }],
        administrateur_id: formData.administrateur_id || user?.id || '',
      }, { headers });
      if (isEdit) {
        try { await axios.post(`${API_URL}/dossiers/${id}/af-liees`, { af_id: resp.data.id }, { headers }); } catch (e) { /* silencieux */ }
      }
      setAllDossiers(prev => [...prev, resp.data].sort((a, b) => (a.code || '').localeCompare(b.code || '')));
      setFormData(prev => ({ ...prev, af_liees: [...(prev.af_liees || []), resp.data.id] }));
      toast.success(`Action ${resp.data.code} créée et rattachée`);
      const dateExpresse = newAfLiee.date_debut;
      setNewAfLiee({ intitule: '', formation_id: '', date_debut: format(new Date(), 'yyyy-MM-dd') });
      setAfLieeDialogOpen(false);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : "Erreur lors de la création de l'action");
    } finally {
      setCreatingAfLiee(false);
    }
  };
  const [newLieu, setNewLieu] = useState({ nom: '', adresse: '', ville: '', code_postal: '' });
  const [creatingLieu, setCreatingLieu] = useState(false);
  const [lieuDialogTarget, setLieuDialogTarget] = useState(null); // 'principal' or date string
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Dialog states for creating new formation
  const [formationDialogOpen, setFormationDialogOpen] = useState(false);
  const [newFormation, setNewFormation] = useState({
    code: '',
    titre: '',
    type_formation: 'presentiel',
    modules: [],
    programme: '',
    duree_totale_heures: 0
  });
  const [creatingFormation, setCreatingFormation] = useState(false);

  // Date range selection for planning
  const [rangeStart, setRangeStart] = useState(null);
  const [rangeEnd, setRangeEnd] = useState(null);
  const [rangeDialogOpen, setRangeDialogOpen] = useState(false);
  const [excludeWeekends, setExcludeWeekends] = useState(true);
  const [editingDay, setEditingDay] = useState(null);
  
  // Shift+click range selection on calendar
  const [lastClickedDate, setLastClickedDate] = useState(null);

  // Dialog states for creating new client
  const [clientDialogOpen, setClientDialogOpen] = useState(false);
  const [newClient, setNewClient] = useState({
    nom: '',
    email: '',
    telephone: '',
    ville: ''
  });
  const [creatingClient, setCreatingClient] = useState(false);

  // Dialog states for creating new financeur
  const [financeurDialogOpen, setFinanceurDialogOpen] = useState(false);
  const [newFinanceur, setNewFinanceur] = useState({
    nom: '',
    type_financeur: 'opco',
    email: '',
    telephone: ''
  });
  const [creatingFinanceur, setCreatingFinanceur] = useState(false);

  // Dialog and state for adding candidates
  const [candidatDialogOpen, setCandidatDialogOpen] = useState(false);
  const [listeCandidatsOpen, setListeCandidatsOpen] = useState(false);
  const [newCandidat, setNewCandidat] = useState({ civilite: '', nom: '', prenom: '', email: '', telephone: '', date_naissance: '' });
  const [creatingCandidat, setCreatingCandidat] = useState(false);
  const [selectedCandidatToAdd, setSelectedCandidatToAdd] = useState('');
  const [expandedCandidatId, setExpandedCandidatId] = useState(null);
  const [suiviCandidats, setSuiviCandidats] = useState({});
  useEffect(() => {
    if (!id) return;
    axios.get(`${API_URL}/portail/formateur/action/${id}/candidats-suivi`).then(r => setSuiviCandidats(Object.fromEntries((r.data.candidats || []).map(c => [c.candidat_id, c])))).catch(() => {});
  }, [id]);

  // Import candidats states
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importParsed, setImportParsed] = useState([]);
  const [importLoading, setImportLoading] = useState(false);
  const [importConfirming, setImportConfirming] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  // Disponibilité des formateurs par date
  const [disponibiliteCache, setDisponibiliteCache] = useState({});
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'general');

  const fetchDisponibilite = async (date) => {
    try {
      const params = id ? `?exclude_dossier_id=${id}` : '';
      const res = await axios.get(`${API_URL}/formateurs/disponibilite/${date}${params}`);
      setDisponibiliteCache(prev => ({ ...prev, [date]: res.data }));
    } catch (error) {
      // erreur silencieuse
    }
  };

  const getFormateurDispo = (formateurId, date, demiJournee) => {
    const data = disponibiliteCache[date];
    if (!data) return null;
    const f = data.find(d => d.id === formateurId);
    if (!f) return null;
    const d = f.disponibilite;
    if (!d.sous_contrat) return { status: 'no_contract', label: 'Pas de contrat' };
    
    // Check if already assigned elsewhere (not blocking, just informational)
    const isAssigned = (demiJournee === 'matin' && d.deja_affecte_matin) ||
                       (demiJournee === 'apres_midi' && d.deja_affecte_apres_midi) ||
                       (demiJournee === 'journee' && d.deja_affecte_journee);
    
    if (isAssigned && d.affectations?.length > 0) {
      const dossiersList = d.affectations.map(a => a.dossier_code).join(', ');
      return { status: 'multi', label: `Aussi sur ${dossiersList}`, affectations: d.affectations };
    }
    return { status: 'available', label: 'Disponible' };
  };

  // Référent pédagogique par défaut à la création : le créateur s'il a la fonction référent pédagogique
  useEffect(() => {
    if (isEdit || formData.referent_id || !user?.formateur_id) return;
    const moi = formateurs.find(f => f.id === user.formateur_id && (f.fonctions || []).includes('referent_pedagogique'));
    if (moi) setFormData(prev => ({ ...prev, referent_id: moi.id }));
  }, [formateurs, isEdit, user, formData.referent_id]);

  // Automatically update date_fin based on the last date in planning
  useEffect(() => {
    if (formData.planning.length > 0) {
      const dates = formData.planning.map(p => parseISO(p.date));
      const lastDate = max(dates);
      const newDateFin = format(lastDate, 'yyyy-MM-dd');
      
      if (formData.date_fin !== newDateFin) {
        setFormData(prev => ({ ...prev, date_fin: newDateFin }));
      }
    }
  }, [formData.planning, formData.date_fin]);

  // Memoized sorted planning and available candidats to avoid expensive re-computations
  const horaires = useMemo(() => ({ ...HORAIRES_DEFAUT, ...(formData.horaires || {}), ...(!(formData.horaires || {}).matin_debut && formData.heure_debut ? { matin_debut: formData.heure_debut } : {}) }), [formData.horaires, formData.heure_debut]);
  const sortedPlanning = useMemo(
    () => [...formData.planning].sort((a, b) => a.date.localeCompare(b.date)),
    [formData.planning]
  );

  const availableCandidats = useMemo(
    () => candidats
      .filter(c => !formData.candidats.some(fc => fc.candidat_id === c.id))
      .sort((a, b) => (a.nom || '').localeCompare(b.nom || '') || (a.prenom || '').localeCompare(b.prenom || '')),
    [candidats, formData.candidats]
  );

  const fetchData = useCallback(async () => {
    try {
      const [formationsRes, candidatsRes, formateursRes, modulesRes, clientsRes, financeursRes, lieuxRes, dossiersRes] = await Promise.all([
        axios.get(`${API_URL}/formations`, { headers }),
        axios.get(`${API_URL}/candidats`, { headers }),
        axios.get(`${API_URL}/formateurs`, { headers }),
        axios.get(`${API_URL}/modules`, { headers }),
        axios.get(`${API_URL}/clients`, { headers }),
        axios.get(`${API_URL}/financeurs`, { headers }),
        axios.get(`${API_URL}/lieux`, { headers }),
        axios.get(`${API_URL}/dossiers`, { headers })
      ]);

      setFormations([...formationsRes.data].sort((a, b) => (a.code || '').localeCompare(b.code || '')));
      setCandidats(candidatsRes.data);
      setFormateurs(formateursRes.data);
      setModules(modulesRes.data);
      setClients(clientsRes.data);
      setFinanceurs(financeursRes.data);
      setLieux(lieuxRes.data);
      setAllDossiers([...dossiersRes.data].sort((a, b) => (a.code || '').localeCompare(b.code || '')));

      if (isEdit) {
        const [dossierRes, dfSummaryRes] = await Promise.all([
          axios.get(`${API_URL}/dossiers/${id}`, { headers }),
          axios.get(`${API_URL}/dossiers-financeurs?action_id=${id}`, { headers }),
        ]);
        setFormData(prev => ({
          ...prev,
          ...dossierRes.data,
          plan_financement: dossierRes.data.plan_financement || null,
          bons_de_commande: dossierRes.data.bons_de_commande || [],
          code_action_client: dossierRes.data.code_action_client || '',
          type_formation: dossierRes.data.type_formation || 'continue',
          statut_administratif: dossierRes.data.statut_administratif || 'brouillon',
          pieces_justificatives: dossierRes.data.pieces_justificatives || [],
          conditions_facturation: dossierRes.data.conditions_facturation || 'fin_session',
          af_liees: dossierRes.data.af_liees || [],
        }));
        setDossiersFinanceursSummary(dfSummaryRes.data);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setFetching(false);
    }
  }, [id, isEdit, headers]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleDateChange = (field, date) => {
    setFormData({ 
      ...formData, 
      [field]: date ? format(date, 'yyyy-MM-dd') : '' 
    });
  };

  const handleAddCandidat = () => {
    if (!selectedCandidatToAdd || selectedCandidatToAdd === 'none') return;
    const alreadyAdded = formData.candidats.some(c => c.candidat_id === selectedCandidatToAdd);
    if (alreadyAdded) {
      toast.error('Ce candidat est déjà affecté');
      return;
    }
    setFormData({
      ...formData,
      candidats: [...formData.candidats, { candidat_id: selectedCandidatToAdd, dates: [], presences: {} }]
    });
    setSelectedCandidatToAdd('');
    toast.success('Candidat ajouté');
  };

  const handleRemoveCandidat = (candidatId) => {
    setFormData({
      ...formData,
      candidats: formData.candidats.filter(c => c.candidat_id !== candidatId)
    });
    if (expandedCandidatId === candidatId) setExpandedCandidatId(null);
  };

  const handleCandidatDateToggle = (candidatId, dateStr) => {
    setFormData(prev => ({
      ...prev,
      candidats: prev.candidats.map(c => {
        if (c.candidat_id !== candidatId) return c;
        const currentDates = c.dates || [];
        if (currentDates.length === 0) {
          // Currently "all dates" - switch to all dates EXCEPT this one
          const allDates = prev.planning.map(p => p.date);
          return { ...c, dates: allDates.filter(d => d !== dateStr) };
        }
        if (currentDates.includes(dateStr)) {
          return { ...c, dates: currentDates.filter(d => d !== dateStr) };
        }
        return { ...c, dates: [...currentDates, dateStr].sort() };
      })
    }));
  };

  const handleCandidatAllDates = (candidatId) => {
    setFormData(prev => ({
      ...prev,
      candidats: prev.candidats.map(c =>
        c.candidat_id === candidatId ? { ...c, dates: [] } : c
      )
    }));
  };

  const [duplicatesFound, setDuplicatesFound] = useState([]);

  const refreshDfSummary = useCallback(async () => {
    if (!id) return;
    try {
      const res = await axios.get(`${API_URL}/dossiers-financeurs?action_id=${id}`, { headers });
      setDossiersFinanceursSummary(res.data);
    } catch {}
  }, [id, headers]);

  const handleCheckAndCreateCandidat = async () => {
    if (!newCandidat.nom || !newCandidat.prenom || !newCandidat.email) {
      toast.error('Nom, prénom et email sont obligatoires');
      return;
    }
    setCreatingCandidat(true);
    try {
      // Check for duplicates first
      const checkRes = await axios.post(`${API_URL}/candidats/check-duplicate`, newCandidat, { headers });
      if (checkRes.data.duplicates?.length > 0) {
        setDuplicatesFound(checkRes.data.duplicates);
        setCreatingCandidat(false);
        return;
      }
      // No duplicates, create directly
      await handleForceCreateCandidat();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la vérification');
      setCreatingCandidat(false);
    }
  };

  const handleForceCreateCandidat = async () => {
    setCreatingCandidat(true);
    try {
      const res = await axios.post(`${API_URL}/candidats`, newCandidat, { headers });
      setCandidats([...candidats, res.data]);
      setFormData(prev => ({
        ...prev,
        candidats: [...prev.candidats, { candidat_id: res.data.id, dates: [], presences: {} }]
      }));
      setCandidatDialogOpen(false);
      setNewCandidat({ civilite: '', nom: '', prenom: '', email: '', telephone: '', date_naissance: '' });
      setDuplicatesFound([]);
      toast.success(`${res.data.nom} ${res.data.prenom} créé et ajouté`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setCreatingCandidat(false);
    }
  };

  const handleUseDuplicate = (dup) => {
    // Add existing candidate to the dossier instead of creating a new one
    if (formData.candidats.some(fc => fc.candidat_id === dup.id)) {
      toast.error(`${dup.nom} ${dup.prenom} est déjà affecté à cette action`);
      return;
    }
    setFormData(prev => ({
      ...prev,
      candidats: [...prev.candidats, { candidat_id: dup.id, dates: [], presences: {} }]
    }));
    if (!candidats.find(c => c.id === dup.id)) {
      setCandidats(prev => [...prev, dup]);
    }
    setCandidatDialogOpen(false);
    setNewCandidat({ civilite: '', nom: '', prenom: '', email: '', telephone: '', date_naissance: '' });
    setDuplicatesFound([]);
    toast.success(`${dup.nom} ${dup.prenom} (existant) ajouté au dossier`);
  };

  // Import candidats from file
  const isEmailValid = (email) => !email || /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email);
  const handleFileImport = async (file) => {
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['csv', 'xlsx', 'xls'].includes(ext)) {
      toast.error('Format non supporté. Utilisez .csv ou .xlsx');
      return;
    }
    setImportLoading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      const res = await axios.post(`${API_URL}/candidats/import`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setImportParsed(res.data.parsed.map(p => ({ ...p, selected: true, _uid: crypto.randomUUID ? crypto.randomUUID() : `${Math.random()}` })));
      setImportDialogOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la lecture du fichier');
    } finally {
      setImportLoading(false);
    }
  };

  const handleDropFile = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFileImport(file);
  };

  const handleConfirmImport = async () => {
    const selected = importParsed.filter(p => p.selected);
    if (selected.length === 0) { toast.error('Aucun candidat sélectionné'); return; }
    setImportConfirming(true);
    try {
      const entries = selected.map(p => ({
        civilite: p.civilite || null, nom: p.nom, prenom: p.prenom, email: p.email,
        telephone: p.telephone, adresse: p.adresse,
        date_naissance: p.date_naissance, entreprise: p.entreprise,
        existing_id: p.is_duplicate ? p.existing_id : null,
      }));
      const res = await axios.post(`${API_URL}/candidats/import/confirm`, { entries });
      // Refresh candidats list
      const candidatsRes = await axios.get(`${API_URL}/candidats`);
      setCandidats(candidatsRes.data);
      // Add all imported candidates to the dossier
      const newCandidatIds = res.data.results.map(r => r.id);
      const existingIds = formData.candidats.map(c => c.candidat_id);
      const toAdd = newCandidatIds.filter(id => !existingIds.includes(id));
      setFormData(prev => ({
        ...prev,
        candidats: [...prev.candidats, ...toAdd.map(id => ({ candidat_id: id, dates: [], presences: {} }))]
      }));
      toast.success(`${res.data.created} créé(s), ${res.data.existing} existant(s) — ${toAdd.length} ajouté(s) au dossier`);
      setImportDialogOpen(false);
      setImportParsed([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'import");
    } finally {
      setImportConfirming(false);
    }
  };

  // Create new formation
  const handleCreateFormation = async () => {
    if (!newFormation.code || !newFormation.titre) {
      toast.error('Le code et le titre sont obligatoires');
      return;
    }

    setCreatingFormation(true);
    try {
      const response = await axios.post(`${API_URL}/formations`, newFormation);
      const createdFormation = response.data;
      
      // Add to formations list and select it
      setFormations([...formations, createdFormation].sort((a, b) => (a.code || '').localeCompare(b.code || '')));
      setFormData({ ...formData, formation_id: createdFormation.id });
      
      // Reset and close dialog
      setNewFormation({
        code: '',
        titre: '',
        type_formation: 'presentiel',
        modules: [],
        programme: '',
        duree_totale_heures: 0
      });
      setFormationDialogOpen(false);
      toast.success('Formation créée et sélectionnée');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création de la formation');
    } finally {
      setCreatingFormation(false);
    }
  };

  // Create new client
  const handleCreateClient = async () => {
    if (!newClient.nom) {
      toast.error('Le nom du client est obligatoire');
      return;
    }

    setCreatingClient(true);
    try {
      const response = await axios.post(`${API_URL}/clients`, newClient);
      const createdClient = response.data;
      
      // Add to clients list and select it
      setClients([...clients, createdClient]);
      setFormData({ ...formData, client_id: createdClient.id, client: createdClient.nom });
      
      // Reset and close dialog
      setNewClient({ nom: '', email: '', telephone: '', ville: '' });
      setClientDialogOpen(false);
      toast.success('Client créé et sélectionné');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du client');
    } finally {
      setCreatingClient(false);
    }
  };

  // Create new financeur
  const handleCreateFinanceur = async () => {
    if (!newFinanceur.nom) {
      toast.error('Le nom du financeur est obligatoire');
      return;
    }

    setCreatingFinanceur(true);
    try {
      const response = await axios.post(`${API_URL}/financeurs`, newFinanceur);
      const createdFinanceur = response.data;
      
      // Add to financeurs list and select it
      setFinanceurs([...financeurs, createdFinanceur]);
      setFormData({ ...formData, financeur_id: createdFinanceur.id, financeur: createdFinanceur.nom });
      
      // Reset and close dialog
      setNewFinanceur({ nom: '', type_financeur: 'opco', email: '', telephone: '' });
      setFinanceurDialogOpen(false);
      toast.success('Financeur créé et sélectionné');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du financeur');
    } finally {
      setCreatingFinanceur(false);
    }
  };

  const handleCreateLieu = async () => {
    if (!newLieu.nom) {
      toast.error('Le nom du lieu est obligatoire');
      return;
    }
    setCreatingLieu(true);
    try {
      const response = await axios.post(`${API_URL}/lieux`, newLieu);
      const createdLieu = response.data;
      setLieux(prev => [...prev, createdLieu]);
      
      // Auto-select the new lieu in the correct target
      if (lieuDialogTarget === 'principal') {
        setFormData(prev => ({ ...prev, lieu_principal: createdLieu.nom }));
      } else if (lieuDialogTarget === 'secondaire') {
        setFormData(prev => ({ ...prev, lieu_secondaire: createdLieu.nom }));
      } else if (lieuDialogTarget) {
        // lieuDialogTarget is the date string for a planning day
        setFormData(prev => ({
          ...prev,
          planning: prev.planning.map(p =>
            p.date === lieuDialogTarget ? { ...p, lieu: createdLieu.nom } : p
          )
        }));
      }
      
      setNewLieu({ nom: '', adresse: '', ville: '', code_postal: '' });
      setLieuDialogOpen(false);
      setLieuDialogTarget(null);
      toast.success('Lieu créé et sélectionné');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du lieu');
    } finally {
      setCreatingLieu(false);
    }
  };

  // Handle client selection
  const handleClientChange = (clientId) => {
    const selectedClient = clients.find(c => c.id === clientId);
    setFormData({ 
      ...formData, 
      client_id: clientId, 
      client: selectedClient?.nom || '' 
    });
  };

  // Handle financeur selection
  const handleFinanceurChange = (financeurId) => {
    if (financeurId === 'same_as_client') {
      // Use client as financeur
      const selectedClient = clients.find(c => c.id === formData.client_id);
      setFormData({ 
        ...formData, 
        financeur_id: '', 
        financeur: selectedClient?.nom || formData.client 
      });
    } else {
      const selectedFinanceur = financeurs.find(f => f.id === financeurId);
      setFormData({ 
        ...formData, 
        financeur_id: financeurId, 
        financeur: selectedFinanceur?.nom || '' 
      });
    }
  };

  // Add date range to planning
  const handleAddDateRange = () => {
    if (!rangeStart || !rangeEnd) {
      toast.error('Veuillez sélectionner une date de début et de fin');
      return;
    }

    const start = new Date(rangeStart);
    const end = new Date(rangeEnd);
    
    if (start > end) {
      toast.error('La date de début doit être avant la date de fin');
      return;
    }

    const newDates = [];
    const current = new Date(start);
    
    while (current <= end) {
      const dayOfWeek = current.getDay();
      // Skip weekends if excludeWeekends is true (0 = Sunday, 6 = Saturday)
      if (!excludeWeekends || (dayOfWeek !== 0 && dayOfWeek !== 6)) {
        const dateStr = format(current, 'yyyy-MM-dd');
        if (!formData.planning.find(p => p.date === dateStr)) {
          newDates.push({
            date: dateStr,
            demi_journee: 'journee',
            formateur_id: '',
            module_id: ''
          });
        }
      }
      current.setDate(current.getDate() + 1);
    }

    if (newDates.length === 0) {
      toast.info('Toutes les dates de cette période sont déjà sélectionnées');
      return;
    }

    setFormData({ 
      ...formData, 
      planning: [...formData.planning, ...newDates].sort((a, b) => a.date.localeCompare(b.date)) 
    });
    
    setRangeStart(null);
    setRangeEnd(null);
    setRangeDialogOpen(false);
    toast.success(`${newDates.length} jour(s) ajouté(s) au planning`);
  };

  const isLocked = formData.statut === 'termine' || formData.statut === 'cloture';
  const dateFinDepassee = !!formData.date_fin && formData.date_fin < format(new Date(), 'yyyy-MM-dd');
  const isAbandoned = formData.statut === 'abandon';

  const handleDeleteAction = async () => {
    try {
      await axios.delete(`${API_URL}/dossiers/${id}`, { headers });
      toast.success(formData.type_dossier === 'aft' ? 'Action de certification supprimée' : 'Action de formation supprimée');
      navigate(formData.type_dossier === 'aft' && formData.af_mere_id ? `/dossiers/${formData.af_mere_id}` : '/dossiers');
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleCloneAction = async () => {
    try {
      const res = await axios.post(`${API_URL}/dossiers/${id}/clone`, {}, { headers });
      toast.success(`Action clonée : ${res.data.code}`);
      navigate(`/dossiers/${res.data.id}`);
    } catch (err) {
      toast.error('Erreur lors du clonage');
    }
  };

  const handleImportDates = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API_URL}/dossiers/import-dates`, fd, { headers });
      const existing = new Set(formData.planning.map(p => p.date));
      const newDates = res.data.dates.filter(d => !existing.has(d)).map(d => ({ date: d, demi_journee: 'journee', formateur_id: '', module_id: '' }));
      const skipped = res.data.dates.length - newDates.length;
      if (newDates.length === 0) {
        toast.info('Toutes les dates du fichier sont déjà dans le planning');
        return;
      }
      setFormData(prev => ({ ...prev, planning: [...prev.planning, ...newDates].sort((a, b) => a.date.localeCompare(b.date)) }));
      toast.success(`${newDates.length} date(s) importée(s)${skipped > 0 ? `, ${skipped} déjà présente(s)` : ''}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'import des dates');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.formation_id && formData.type_dossier !== 'aft') {
      toast.error('Veuillez sélectionner ou créer une formation');
      return;
    }
    
    if (!formData.date_debut) {
      toast.error('Veuillez sélectionner une date de début');
      return;
    }

    setLoading(true);

    try {
      const dataToSend = {
        ...formData,
        administrateur_id: formData.administrateur_id || user?.id
      };

      if (isEdit) {
        await axios.put(`${API_URL}/dossiers/${id}`, dataToSend, { params: deverrouilles.length ? { deverrouilles: deverrouilles.join(',') } : {} });
        setDeverrouilles([]);
        toast.success('Dossier mis à jour');
        if (searchParams.get('from') === 'apercu') navigate(`/dossiers/${id}`);
      } else {
        const response = await axios.post(`${API_URL}/dossiers`, dataToSend);
        toast.success('Dossier créé');
        // Naviguer vers la page d'édition du nouveau dossier
        navigate(`/dossiers/${response.data.id}/edit`, { replace: true });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const getFormateurName = (id) => {
    if (!id) return 'Non défini';
    const f = formateurs.find(f => f.id === id);
    return f ? `${f.nom} ${f.prenom}` : 'Inconnu';
  };

  const getModuleName = (id) => {
    if (!id) return 'Non défini';
    const m = modules.find(m => m.id === id);
    return m ? m.titre : 'Inconnu';
  };

  if (fetching) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </DashboardLayout>
    );
  }

  if (isEdit && formData.type_dossier === 'aft') {
    return (
      <DashboardLayout>
        <AftPage dossierId={id} canManage={['admin', 'super_admin', 'responsable_pedagogique', 'administratif'].includes(user?.role)} onDelete={() => setDeleteDialogOpen(true)} />
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader><AlertDialogTitle>Supprimer l'action de certification {formData.code} ?</AlertDialogTitle>
              <AlertDialogDescription>Les candidats délégués resteront sur l'AF mère. Cette action est irréversible.</AlertDialogDescription></AlertDialogHeader>
            <AlertDialogFooter><AlertDialogCancel>Annuler</AlertDialogCancel><AlertDialogAction onClick={handleDeleteAction} className="bg-red-600 hover:bg-red-700" data-testid="aft-confirm-delete">Supprimer</AlertDialogAction></AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto space-y-6" data-testid="dossier-form-page">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          <div className="flex items-start sm:items-center gap-3 sm:gap-4 min-w-0">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">
                  {isEdit ? (formData.type_dossier === 'aft' ? 'Action de certification' : 'Action de formation') : 'Nouvelle action de formation'}
                </h1>
                {isEdit && formData.code && (
                  <span className="px-2 py-1 bg-indigo-100 text-indigo-700 text-sm font-mono rounded">
                    {formData.code}
                  </span>
                )}
                {isEdit && (
                  <span className={`px-2.5 py-1 text-xs font-semibold rounded-full ${
                    formData.statut === 'en_cours' ? 'bg-blue-100 text-blue-700' :
                    formData.statut === 'termine' ? 'bg-emerald-100 text-emerald-700' :
                    formData.statut === 'cloture' ? 'bg-slate-200 text-slate-700' :
                    formData.statut === 'abandon' ? 'bg-red-100 text-red-700' :
                    'bg-slate-100 text-slate-600'
                  }`} data-testid="action-statut-badge">
                    {formData.statut === 'en_cours' ? (dateFinDepassee ? 'En cours (date de fin dépassée)' : 'En cours') : formData.statut === 'termine' ? 'Terminé' : formData.statut === 'cloture' ? 'Clôturé' : formData.statut === 'abandon' ? 'Abandon' : formData.statut}
                  </span>
                )}
              </div>
              {isEdit && formData.intitule && (
                <p className="text-lg font-semibold text-slate-800 mt-1" data-testid="header-af-intitule">
                  {formData.intitule}
                </p>
              )}
              {isEdit && formData.formation_id && (() => {
                const f = formations.find(fo => fo.id === formData.formation_id);
                return f ? (
                  <p className="text-sm mt-0.5">
                    <span className="text-slate-500">Programme de formation : </span>
                    <button
                      type="button"
                      onClick={() => window.open(`/formations?view=${formData.formation_id}`, '_blank')}
                      title="Voir la fiche de la formation"
                      className="font-semibold text-indigo-600 hover:text-indigo-800 hover:underline text-left transition-colors"
                      data-testid="header-formation-titre"
                    >
                      {f.code} — {f.titre}
                    </button>
                  </p>
                ) : null;
              })()}
              {isEdit && (() => {
                const codesClients = [...new Set([
                  ...dossiersFinanceursSummary.map(df => df.code_action_client).filter(Boolean),
                  ...(formData.code_action_client ? [formData.code_action_client] : []),
                ])];
                return codesClients.length > 0 ? (
                  <p className="text-sm mt-0.5" data-testid="header-code-client">
                    <span className="text-slate-500">Code client : </span>
                    <span className="font-mono font-semibold text-purple-600">{codesClients.join(', ')}</span>
                  </p>
                ) : null;
              })()}
              {!isEdit && (
                <p className="text-slate-500 mt-0.5 text-sm">Créez une nouvelle action de formation</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {isEdit && (
              <Button type="button" variant="outline" onClick={() => navigate(`/dossiers/${id}`)} data-testid="btn-retour-apercu">
                <ArrowLeft className="w-4 h-4 mr-1" /> Aperçu de l'action
              </Button>
            )}
            {isEdit && (
              <Button
                type="button"
                variant="outline"
                className="text-red-500 border-red-200 hover:bg-red-50 hover:text-red-700"
                onClick={() => setDeleteDialogOpen(true)}
                data-testid="delete-action-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Supprimer
              </Button>
            )}
            {isEdit && (
              <Button
                type="button"
                variant="outline"
                className="text-indigo-600 border-indigo-200 hover:bg-indigo-50 hover:text-indigo-700"
                onClick={handleCloneAction}
                data-testid="clone-action-btn"
              >
                <CopyIcon className="w-4 h-4 mr-2" />
                Cloner
              </Button>
            )}
            <Button 
              variant="outline" 
              onClick={() => navigate('/dossiers')} 
              data-testid="view-all-dossiers-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voir toutes les actions
            </Button>
            <Button
              type="submit"
              form="dossier-form"
              className="bg-indigo-600 hover:bg-indigo-700"
              disabled={loading || isLocked}
              data-testid="submit-dossier-top"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {isLocked ? 'Action verrouillée' : isEdit ? 'Mettre à jour' : 'Créer l\'action'}
            </Button>
          </div>
        </div>

        <form id="dossier-form" onSubmit={handleSubmit}>
          {formData.type_dossier === 'aft' && (
            <div className="mb-4 bg-cyan-50 border border-cyan-200 rounded-lg px-4 py-3 flex items-center gap-3" data-testid="banner-aft">
              <span className="text-xs font-bold text-cyan-700 bg-cyan-100 px-2 py-1 rounded">ACTION DE CERTIFICATION (AFT)</span>
              <p className="text-sm text-cyan-800">
                Cette action de certification fait passer le module certifiant en dehors de l'AF mère
                {formData.af_mere_code && (
                  <>
                    {' '}— AF mère :{' '}
                    <button
                      type="button"
                      className="font-semibold underline hover:text-cyan-600"
                      onClick={() => navigate(`/dossiers/${formData.af_mere_id}/edit`)}
                      data-testid="link-af-mere"
                    >
                      {formData.af_mere_code}
                    </button>
                  </>
                )}
                . Affectez un testeur ou responsable de session sur les journées et inscrivez les candidats. Les notations remonteront dans l'AF mère.
              </p>
            </div>
          )}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 pb-1">
            <TabsList className="bg-slate-100 w-max">
              <TabsTrigger value="general">Informations générales</TabsTrigger>
              {!isAbandoned && (
                <TabsTrigger value="candidats">Candidats ({formData.candidats.filter(a => !a.abandon).length})</TabsTrigger>
              )}
              {!isAbandoned && (
                <TabsTrigger value="planning">Planning ({formData.planning.length} jour{formData.planning.length > 1 ? 's' : ''})</TabsTrigger>
              )}
              {!isAbandoned && isEdit && formData.planning.length > 0 && formData.formation_id && (
                <TabsTrigger value="planning_pedagogique" data-testid="tab-planning-pedagogique">Planning pédagogique</TabsTrigger>
              )}
              {!isAbandoned && isEdit && (
                <TabsTrigger value="fil_chronologique" data-testid="tab-fil-chronologique">Fil chronologique</TabsTrigger>
              )}
              {!isAbandoned && isEdit && (
                <TabsTrigger value="rapports" data-testid="tab-rapports">Rapports</TabsTrigger>
              )}
              {!isAbandoned && isEdit && (
                <TabsTrigger value="bilan" data-testid="tab-bilan">Bilan</TabsTrigger>
              )}
              {!isAbandoned && isEdit && (
                <TabsTrigger value="financement" data-testid="tab-financement">Dossiers financeurs</TabsTrigger>
              )}
              {!isAbandoned && isEdit && voirCouts && (
                <TabsTrigger value="facturation" data-testid="tab-facturation">Facturation</TabsTrigger>
              )}
              {!isAbandoned && isEdit && (
                <TabsTrigger value="workflow" data-testid="tab-workflow">Workflow</TabsTrigger>
              )}
            </TabsList>
            </div>

            {/* General Info Tab */}
            <TabsContent value="general">
              <Card className="border-0 shadow-sm">
                <CardContent className="p-6 space-y-6">
                  {/* Statut de l'action */}
                  {isEdit && (
                    <div className="flex items-center justify-between p-3 rounded-lg border bg-slate-50">
                      <div className="flex items-center gap-3">
                        <Label className="text-sm font-semibold text-slate-700">Statut de l'action</Label>
                        <Select
                          value={formData.statut || 'en_cours'}
                          onValueChange={async (v) => {
                            if (v === 'termine' || v === 'cloture' || v === 'abandon') {
                              if (!window.confirm(v === 'termine'
                                ? 'Passer en "Terminé" verrouillera toutes les modifications. Confirmer ?'
                                : v === 'cloture'
                                  ? 'Clôturer l\'action la verrouillera définitivement (plus aucune modification). Confirmer ?'
                                  : 'Passer en "Abandon" masquera le planning et les candidats. Confirmer ?'
                              )) return;
                            }
                            const newData = { ...formData, statut: v };
                            setFormData(newData);
                            // Auto-save status change
                            if (isEdit) {
                              try {
                                await axios.patch(`${API_URL}/dossiers/${id}/statut`, { statut: v });
                                toast.success(v === 'termine' ? 'Action marquée comme terminée' : v === 'cloture' ? 'Action clôturée' : v === 'abandon' ? 'Action marquée en abandon' : 'Statut mis à jour');
                              } catch { toast.error('Erreur lors du changement de statut'); }
                            }
                          }}
                        >
                          <SelectTrigger className="w-[160px] h-8 text-xs" data-testid="select-statut-action">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="en_cours">En cours</SelectItem>
                            {(!dateFinDepassee || formData.statut === 'termine') && <SelectItem value="termine">Terminé</SelectItem>}
                            <SelectItem value="abandon">Abandon</SelectItem>
                            {dateFinDepassee && <SelectItem value="cloture">Clôturé</SelectItem>}
                          </SelectContent>
                        </Select>
                      </div>
                      {isLocked && (
                        <span className="text-xs text-amber-600 font-medium flex items-center gap-1">
                          <AlertTriangle className="w-3.5 h-3.5" /> Action verrouillée
                        </span>
                      )}
                      {isAbandoned && (
                        <span className="text-xs text-red-600 font-medium flex items-center gap-1">
                          <AlertTriangle className="w-3.5 h-3.5" /> Action abandonnée
                        </span>
                      )}
                    </div>
                  )}

                  <fieldset disabled={isLocked} className="space-y-6 min-w-0">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 [&>*]:min-w-0">
                    <div className="md:col-span-2 space-y-2">
                      <Label htmlFor="formation_id">Formation *</Label>
                      <div className="flex gap-2 min-w-0">
                        <FormationPicker
                          formations={formations}
                          value={formData.formation_id}
                          disabled={isLocked}
                          testId="select-formation"
                          className="flex-1"
                          onChange={(value, f) => {
                            const oldF = formations.find(fo => fo.id === formData.formation_id);
                            setFormData(prev => ({
                              ...prev,
                              formation_id: value,
                              intitule: f && (!prev.intitule.trim() || (oldF && prev.intitule === oldF.titre)) ? f.titre : prev.intitule,
                            }));
                          }}
                        />
                        {formData.formation_id && (
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={() => window.open(`/formations?edit=${formData.formation_id}`, '_blank')}
                            title="Modifier la fiche formation (modules, durée...)"
                            data-testid="btn-edit-formation"
                          >
                            <FileCheck className="w-4 h-4 text-indigo-600" />
                          </Button>
                        )}
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => setFormationDialogOpen(true)}
                          title="Créer une nouvelle formation"
                          data-testid="btn-new-formation"
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                      {formations.length === 0 && (
                        <p className="text-xs text-amber-600">
                          Aucune formation existante. Cliquez sur + pour en créer une.
                        </p>
                      )}
                      {formData.formation_id && (() => {
                        const f = formations.find(f => f.id === formData.formation_id);
                        return f ? (
                          <button
                            type="button"
                            onClick={() => window.open(`/formations?view=${formData.formation_id}`, '_blank')}
                            title="Voir la fiche de la formation"
                            className="text-xs text-slate-500 hover:text-indigo-700 hover:underline text-left transition-colors"
                            data-testid="link-view-formation"
                          >
                            <span className="font-mono font-medium text-indigo-600">{f.code}</span>
                            <span className="mx-1">—</span>
                            <span>{f.titre}</span>
                            {f.duree_totale_heures > 0 && <span className="text-slate-400 ml-1">({f.duree_totale_heures}h)</span>}
                          </button>
                        ) : null;
                      })()}
                    </div>

                    <div className="md:col-span-2 space-y-2">
                      <Label htmlFor="intitule">Intitulé du dossier *</Label>
                      <Input
                        id="intitule"
                        name="intitule"
                        value={formData.intitule}
                        onChange={handleChange}
                        placeholder="Choisissez d'abord une formation, l'intitulé se remplira automatiquement (modifiable)"
                        required
                        data-testid="input-intitule"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Type de formation *</Label>
                      <Select
                        value={formData.type_formation || 'continue'}
                        onValueChange={(v) => setFormData({ ...formData, type_formation: v })}
                      >
                        <SelectTrigger data-testid="select-type-formation">
                          <SelectValue placeholder="Type de formation" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="initiale">Formation initiale</SelectItem>
                          <SelectItem value="continue">Formation continue</SelectItem>
                          <SelectItem value="alternance">Formation en alternance</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Date de début *</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal",
                              !formData.date_debut && "text-muted-foreground"
                            )}
                            data-testid="date-debut-btn"
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {formData.date_debut ? format(parseISO(formData.date_debut), 'dd MMMM yyyy', { locale: fr }) : "Sélectionner une date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={formData.date_debut ? parseISO(formData.date_debut) : undefined}
                            onSelect={(date) => handleDateChange('date_debut', date)}
                            locale={fr}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    <div className="space-y-2">
                      <Label>Date de fin</Label>
                      <div className={cn(
                        "flex items-center h-10 w-full rounded-md border border-input bg-slate-50 px-3 py-2 text-sm",
                        formData.date_fin ? "text-slate-900" : "text-muted-foreground"
                      )}>
                        <CalendarIcon className="mr-2 h-4 w-4 text-slate-400" />
                        {formData.date_fin && formData.date_fin.length > 0
                          ? format(parseISO(formData.date_fin), 'dd MMMM yyyy', { locale: fr }) 
                          : "Calculée automatiquement"
                        }
                      </div>
                      <p className="text-xs text-slate-500">
                        {formData.planning.length > 0 
                          ? "Correspond à la dernière date du planning" 
                          : "Sera définie par la dernière date du planning"
                        }
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label>Référent pédagogique</Label>
                      <Select
                        value={formData.referent_id || 'none'}
                        onValueChange={(value) => setFormData({ ...formData, referent_id: value === 'none' ? '' : value })}
                      >
                        <SelectTrigger data-testid="select-referent" className="w-full">
                          <SelectValue placeholder="Sélectionner un référent..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none"><span className="text-slate-400">Aucun</span></SelectItem>
                          {formateurs
                            .filter(f => (f.fonctions || []).includes('referent_pedagogique') && ((f.is_active !== false && f.sous_contrat !== false) || f.id === formData.referent_id))
                            .map(f => <SelectItem key={f.id} value={f.id} data-testid={`referent-option-${f.id}`}>{f.nom} {f.prenom}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-slate-500">Collaborateurs actifs ayant la fonction « Référent pédagogique » — affiché sur les bilans</p>
                    </div>

                    <div className="space-y-2" data-testid="af-commercial-bloc">
                      <Label>Commercial à l'origine du projet</Label>
                      <div className="h-10 flex items-center gap-2 rounded-md border bg-slate-50 px-3 text-sm">
                        <span className="font-medium text-slate-800" data-testid="af-commercial-nom">{formData.commercial_nom || 'admin'}</span>
                        {formData.projet_commercial_id ? <span className="text-[10px] text-indigo-700 bg-indigo-50 px-1.5 py-0.5 rounded">issu d'une demande du portail commercial</span> : <span className="text-[10px] text-slate-400">saisie directe (admin)</span>}
                      </div>
                      <p className="text-xs text-slate-500">Servira au calcul des commissions sur le CA réel.</p>
                    </div>

                    <div className="space-y-2">
                      <Label>Lieu principal de la formation</Label>
                      <div className="flex gap-2">
                        <Select
                          value={formData.lieu_principal || ''}
                          onValueChange={(value) => setFormData({ ...formData, lieu_principal: value })}
                        >
                          <SelectTrigger data-testid="select-lieu-principal" className="flex-1">
                            <SelectValue placeholder="Sélectionner un lieu..." />
                          </SelectTrigger>
                          <SelectContent>
                            {lieux.map(l => (
                              <SelectItem key={l.id} value={l.nom}>
                                {l.nom}{l.adresse ? ` — ${l.adresse}` : ''}{l.ville ? `, ${l.ville}` : ''}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => { setLieuDialogTarget('principal'); setLieuDialogOpen(true); }}
                          data-testid="btn-add-lieu-principal"
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Horaires des journées de formation</Label>
                      <div className="flex flex-wrap items-end gap-4" data-testid="horaires-af">
                        {[['Matin', 'matin_debut', 'matin_fin'], ['Après-midi', 'am_debut', 'am_fin']].map(([lbl, kd, kf]) => (
                          <div key={kd} className="flex items-center gap-2">
                            <span className="text-xs font-medium text-slate-600 w-20">{lbl}</span>
                            <Input type="time" value={horaires[kd]} onChange={(e) => setFormData({ ...formData, horaires: { ...horaires, [kd]: e.target.value }, heure_debut: kd === 'matin_debut' ? e.target.value : (formData.heure_debut || horaires.matin_debut) })} className="w-28" data-testid={`input-${kd}`} />
                            <span className="text-slate-400">→</span>
                            <Input type="time" value={horaires[kf]} onChange={(e) => setFormData({ ...formData, horaires: { ...horaires, [kf]: e.target.value } })} className="w-28" data-testid={`input-${kf}`} />
                          </div>
                        ))}
                        <span className="text-sm font-semibold text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-md px-2 py-1" data-testid="horaires-total">{heuresHoraires(horaires)} h / jour</span>
                      </div>
                      <p className="text-xs text-slate-500">Base horaire appliquée à chaque journée de formation de cette action (retards, départs anticipés, feuilles d'émargement).</p>
                    </div>

                    <div className="space-y-2">
                      <Label>Lieu secondaire</Label>
                      <div className="flex gap-2">
                        <Select
                          value={formData.lieu_secondaire || ''}
                          onValueChange={(value) => setFormData({ ...formData, lieu_secondaire: value })}
                        >
                          <SelectTrigger data-testid="select-lieu-secondaire" className="flex-1">
                            <SelectValue placeholder="Sélectionner un lieu..." />
                          </SelectTrigger>
                          <SelectContent>
                            {lieux.map(l => (
                              <SelectItem key={l.id} value={l.nom}>
                                {l.nom}{l.adresse ? ` — ${l.adresse}` : ''}{l.ville ? `, ${l.ville}` : ''}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => { setLieuDialogTarget('secondaire'); setLieuDialogOpen(true); }}
                          data-testid="btn-add-lieu-secondaire"
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                      <p className="text-xs text-slate-500">
                        Un lieu différent peut être défini par journée dans le Planning pédagogique
                      </p>
                    </div>

                  </div>

                  {/* AF rattachées */}
                  <div className="pt-4 border-t border-slate-100 space-y-2" data-testid="af-liees-section">
                    {isEdit && formData.type_dossier !== 'aft' && (
                      <AftsManquantesInline dossierId={id} onCreated={async (creees) => {
                        try { const r = await axios.get(`${API_URL}/dossiers`); setAllDossiers([...r.data].sort((a, b) => (a.code || '').localeCompare(b.code || ''))); } catch { /* noop */ }
                        setFormData(prev => ({ ...prev, af_liees: [...new Set([...(prev.af_liees || []), ...creees.map(a => a.id)])] }));
                      }} />
                    )}
                    <Label className="text-sm font-semibold text-slate-700">AF rattachées</Label>
                    {isEdit && (() => {
                      const afMeres = allDossiers.filter(d => d.id !== id && (d.af_liees || []).includes(id));
                      return afMeres.length > 0 ? (
                        <div className="flex flex-wrap items-center gap-2 bg-indigo-50 border border-indigo-100 rounded-lg px-3 py-2" data-testid="af-mere-info">
                          <span className="text-xs font-medium text-indigo-700">Cette AF est rattachée à :</span>
                          {afMeres.map(m => (
                            <span
                              key={m.id}
                              role="button"
                              tabIndex={0}
                              onClick={() => navigate(`/dossiers/${m.id}/edit`)}
                              onKeyDown={(e) => e.key === 'Enter' && navigate(`/dossiers/${m.id}/edit`)}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white border border-indigo-200 text-xs text-indigo-700 hover:bg-indigo-100 transition-colors cursor-pointer"
                              title={`Ouvrir ${m.code} — ${m.intitule}`}
                              data-testid={`link-af-mere-${m.id}`}
                            >
                              <span className="font-mono font-semibold">{m.code}</span>
                              <span className="max-w-[220px] truncate">— {m.intitule}</span>
                            </span>
                          ))}
                        </div>
                      ) : null;
                    })()}
                    <p className="text-xs text-slate-500">Les candidats pourront être détachés vers ces actions de formation depuis le détail d'une journée</p>
                    <div className="flex gap-2 items-center">
                      <Select value="" onValueChange={(v) => { if (v && !(formData.af_liees || []).includes(v)) setFormData({ ...formData, af_liees: [...(formData.af_liees || []), v] }); }}>
                        <SelectTrigger data-testid="select-af-liee" className="w-full md:w-[420px]">
                          <SelectValue placeholder="+ Rattacher une action de formation..." />
                        </SelectTrigger>
                        <SelectContent>
                          {allDossiers.filter(d => d.id !== id && !(formData.af_liees || []).includes(d.id)).map(d => (
                            <SelectItem key={d.id} value={d.id}>{d.code} — {d.intitule}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        title="Créer une nouvelle action de formation à rattacher"
                        onClick={() => setAfLieeDialogOpen(true)}
                        data-testid="btn-create-af-liee"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    {(formData.af_liees || []).length > 0 && (
                      <div className="border rounded-lg divide-y overflow-hidden" data-testid="af-liees-badges">
                        {(formData.af_liees || []).map(afId => {
                          const d = allDossiers.find(x => x.id === afId);
                          if (!d) return null;
                          const f = formations.find(fo => fo.id === d.formation_id);
                          return (
                            <div
                              key={afId}
                              className="grid grid-cols-[130px_1fr_1fr_90px_120px_50px_30px] gap-3 items-center px-4 py-2.5 bg-slate-50/60 hover:bg-slate-100 cursor-pointer transition-colors"
                              onClick={() => navigate(`/dossiers/${afId}/edit`)}
                              title={`Ouvrir ${d.code} — ${d.intitule}`}
                              data-testid={`open-af-liee-${afId}`}
                            >
                              <span className="flex items-center gap-1.5 min-w-0">
                                <CornerDownRight className="w-3 h-3 text-slate-400 flex-shrink-0" />
                                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0 bg-violet-400" title="AF rattachée" />
                                <span className="text-xs font-mono text-indigo-600 font-medium truncate">{d.code}</span>
                              </span>
                              <p className="text-sm font-medium text-slate-900 truncate">{d.intitule}</p>
                              <div className="hidden md:flex items-center gap-1.5 min-w-0">
                                <span className="text-sm text-slate-600 truncate">{f?.titre || '—'}</span>
                                {f?.duree_totale_heures > 0 && (
                                  <span className="text-[10px] text-slate-400 flex-shrink-0">{f.duree_totale_heures}h</span>
                                )}
                              </div>
                              <div className="hidden md:block"><StatusBadge status={d.statut} /></div>
                              <span className="text-xs text-slate-400 hidden lg:block">
                                {d.date_debut ? format(parseISO(d.date_debut), 'dd/MM/yy') : '-'}
                                {d.date_fin && ` → ${format(parseISO(d.date_fin), 'dd/MM/yy')}`}
                              </span>
                              <span className="text-sm text-slate-500 text-center hidden lg:block">{d.candidats?.length || 0}</span>
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); setFormData({ ...formData, af_liees: formData.af_liees.filter(x => x !== afId) }); }}
                                className="text-slate-300 hover:text-red-500 font-bold text-base leading-none"
                                title="Détacher cette AF (ne supprime pas l'AF)"
                                data-testid={`remove-af-liee-${afId}`}
                              >×</button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {isEdit && allDossiers.some(d => (d.af_liees || []).includes(id)) && (
                      <p className="text-xs text-violet-600" data-testid="af-rattachee-a">
                        Cette AF est rattachée à : {allDossiers.filter(d => (d.af_liees || []).includes(id)).map(d => d.code).join(', ')}
                      </p>
                    )}
                  </div>

                  {/* Tuiles Dossiers Financeurs */}
                  {isEdit && dossiersFinanceursSummary.length > 0 && (
                    <div className="pt-4 border-t border-slate-100 space-y-3" data-testid="df-summary-tiles">
                      <div className="flex items-center gap-2">
                        <CreditCard className="w-4 h-4 text-indigo-500" />
                        <Label className="text-sm font-semibold text-slate-700">Dossiers financeurs ({dossiersFinanceursSummary.length})</Label>
                      </div>
                      <div className="space-y-2">
                        {dossiersFinanceursSummary.map(df => {
                          const typeLabel = TYPES_FINANCEMENT.find(t => t.value === df.type_financement)?.label || df.type_financement;
                          const modaliteLabel = MODALITES_FACTURATION.find(m => m.value === df.modalite_facturation)?.label || df.modalite_facturation;
                          return (
                            <div
                              key={df.id}
                              className="border border-slate-200 rounded-lg p-4 hover:border-indigo-300 hover:bg-indigo-50/30 transition-colors cursor-pointer group"
                              data-testid={`df-summary-${df.id}`}
                              onClick={() => setActiveTab('financement')}
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="text-xs font-mono text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded">{df.code}</span>
                                    <span className="text-sm font-semibold text-slate-800">{df.financeur_nom || 'Financeur non défini'}</span>
                                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                                      df.statut === 'brouillon' ? 'bg-slate-100 text-slate-600' :
                                      df.statut === 'en_cours' ? 'bg-blue-100 text-blue-700' :
                                      df.statut === 'facture' ? 'bg-amber-100 text-amber-700' :
                                      df.statut === 'regle' ? 'bg-emerald-100 text-emerald-700' :
                                      'bg-red-100 text-red-600'
                                    }`}>
                                      {df.statut === 'brouillon' ? 'Brouillon' : df.statut === 'en_cours' ? 'En cours' : df.statut === 'facture' ? 'Facturé' : df.statut === 'regle' ? 'Réglé' : 'Annulé'}
                                    </span>
                                    {df.code_action_client && (
                                      <span className="text-xs font-mono font-medium text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded">{df.code_action_client}</span>
                                    )}
                                  </div>
                                  <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
                                    <span>{typeLabel}</span>
                                    {df.nature_financement && <span className="text-indigo-500">{df.nature_financement}</span>}
                                    <span>{(df.candidats?.length || 0)} candidat(s)</span>
                                    <span>{modaliteLabel}{voirCouts ? ` — ${df.tarif_unitaire || 0} € HT` : ''}</span>
                                    {df.entreprise_finale && <span>Entreprise: {df.entreprise_finale}</span>}
                                    {(df.bons_de_commande || []).length > 0 && (
                                      <span className="text-orange-600">{df.bons_de_commande.length} BDC</span>
                                    )}
                                  </div>
                                </div>
                                <span className="text-xs text-indigo-500 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">Voir le détail →</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  </fieldset>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Candidats Tab */}
            <TabsContent value="candidats">
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Users className="w-5 h-5 text-indigo-600" />
                    Candidats affectés
                  </CardTitle>
                  <p className="text-sm text-slate-500 mt-1">
                    Par défaut, chaque candidat est inscrit sur toutes les dates du planning. Vous pouvez restreindre à des dates spécifiques.
                  </p>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Add candidate row */}
                  <div className="flex items-center gap-2 flex-wrap" data-testid="add-candidat-row">
                    <Select value={selectedCandidatToAdd} onValueChange={setSelectedCandidatToAdd}
                      onOpenChange={async (open) => {
                        if (open) {
                          try {
                            const res = await axios.get(`${API_URL}/candidats`, { headers });
                            setCandidats(res.data);
                          } catch(e) {}
                        }
                      }}
                    >
                      <SelectTrigger className="flex-1 min-w-[200px]" data-testid="candidat-select">
                        <SelectValue placeholder="Sélectionner un candidat..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">
                          <span className="text-slate-400">Sélectionner un candidat...</span>
                        </SelectItem>
                        {availableCandidats
                          .map(c => (
                            <SelectItem key={c.id} value={c.id}>
                              {c.nom} {c.prenom} - {c.email}
                            </SelectItem>
                          ))
                        }
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      onClick={handleAddCandidat}
                      disabled={!selectedCandidatToAdd || selectedCandidatToAdd === 'none'}
                      className="bg-indigo-600 hover:bg-indigo-700"
                      data-testid="add-candidat-btn"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Ajouter
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setCandidatDialogOpen(true)}
                      data-testid="new-candidat-btn"
                    >
                      <UserPlus className="w-4 h-4 mr-1" />
                      Nouveau
                    </Button>
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept=".csv,.xlsx,.xls"
                        className="hidden"
                        onChange={(e) => handleFileImport(e.target.files[0])}
                        data-testid="import-candidats-input"
                      />
                      <span className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium border rounded-md hover:bg-slate-50 transition-colors text-slate-700 border-slate-200">
                        <Upload className="w-4 h-4" />
                        Importer
                      </span>
                    </label>
                    {/* Button to create user accounts for candidates */}
                    {id && formData.candidats.length > 0 && (
                      <AbandonsDossierDialog
                        dossierId={id}
                        nbAbandons={formData.candidats.filter(a => a.abandon).length}
                        candidatsActifs={sortPersonnes(formData.candidats.filter(a => !a.abandon), (aff) => candidats.find(c => c.id === aff.candidat_id)).map(aff => candidats.find(c => c.id === aff.candidat_id)).filter(Boolean)}
                        onChanged={fetchData}
                      />
                    )}
                    {id && formData.candidats.length > 0 && (
                      <Button
                        type="button"
                        variant="outline"
                        className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                        onClick={async () => {
                          try {
                            const res = await axios.post(`${API_URL}/admin/candidats/creer-comptes-masse`, { dossier_id: id }, { headers });
                            const data = res.data;
                            if (data.created?.length > 0) {
                              toast.success(`${data.created.length} compte(s) créé(s) — mots de passe générés individuellement`);
                            }
                            if (data.skipped?.length > 0) {
                              toast.info(`${data.skipped.length} candidat(s) ignore(s) (compte existant ou sans email)`);
                            }
                            if (data.created?.length === 0 && data.skipped?.length > 0) {
                              toast.info('Tous les candidats ont deja un compte ou pas d\'email');
                            }
                          } catch (err) {
                            toast.error(err.response?.data?.detail || 'Erreur');
                          }
                        }}
                        data-testid="btn-creer-comptes-candidats"
                      >
                        <Key className="w-4 h-4 mr-1" />
                        Creer les comptes
                      </Button>
                    )}
                    {id && (
                      <div className="flex items-center gap-1 ml-auto border rounded-md p-0.5 bg-slate-50" data-testid="liste-candidats-exports">
                        <Button type="button" variant="ghost" size="sm" className="h-8 text-xs" onClick={() => setListeCandidatsOpen(true)} data-testid="btn-liste-candidats-visu"><Eye className="w-3.5 h-3.5 mr-1" /> Liste</Button>
                        <PdfButton url={`${API_URL}/portail/formateur/dossier/${id}/liste-candidats.pdf`} filename={`liste_candidats_${formData.code || ''}.pdf`} className="h-8 text-xs border-0 bg-transparent" testId="btn-liste-candidats-pdf" />
                        <ExcelButton url={`${API_URL}/portail/formateur/dossier/${id}/liste-candidats.xlsx`} filename={`liste_candidats_${formData.code || ''}.xlsx`} className="h-8 text-xs border-0 bg-transparent" testId="btn-liste-candidats-xlsx" />
                      </div>
                    )}
                  </div>
                  {id && <ListeCandidatsDialog dossierId={id} dossierCode={formData.code} open={listeCandidatsOpen} onOpenChange={setListeCandidatsOpen} />}

                  {/* Drag & Drop import zone */}
                  <div
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={handleDropFile}
                    className={cn(
                      "border-2 border-dashed rounded-lg p-4 text-center transition-all",
                      dragOver ? "border-indigo-400 bg-indigo-50" : "border-slate-200 bg-slate-50/50",
                      importLoading && "opacity-50 pointer-events-none"
                    )}
                    data-testid="import-drop-zone"
                  >
                    {importLoading ? (
                      <div className="flex items-center justify-center gap-2 py-2">
                        <Loader2 className="w-5 h-5 animate-spin text-indigo-600" />
                        <span className="text-sm text-indigo-600">Analyse du fichier en cours...</span>
                      </div>
                    ) : (
                      <>
                        <Upload className={cn("w-6 h-6 mx-auto mb-1", dragOver ? "text-indigo-600" : "text-slate-400")} />
                        <p className="text-sm text-slate-600">
                          Glissez-déposez un fichier <strong>CSV</strong> ou <strong>Excel</strong> ici
                        </p>
                        <p className="text-xs text-slate-400 mt-1">
                          Colonnes attendues : Nom, Prénom, Email, Téléphone (optionnel : Adresse, Entreprise, Date de naissance)
                        </p>
                      </>
                    )}
                  </div>

                  {/* Assigned candidates list */}
                  {formData.candidats.length === 0 ? (
                    <div className="text-center py-8 border rounded-lg border-dashed">
                      <Users className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                      <p className="text-sm text-slate-500">Aucun candidat affecté à ce dossier</p>
                      <p className="text-xs text-slate-400 mt-1">Sélectionnez un candidat existant ou créez-en un nouveau</p>
                    </div>
                  ) : (
                    <div className="border rounded-lg divide-y" data-testid="candidats-list">
                      {sortPersonnes(formData.candidats, (aff) => candidats.find(c => c.id === aff.candidat_id)).map((aff) => {
                        const candidat = candidats.find(c => c.id === aff.candidat_id);
                        if (!candidat || aff.abandon) return null;
                        const isExpanded = expandedCandidatId === aff.candidat_id;
                        const isAllDates = !aff.dates || aff.dates.length === 0;
                        const nbDates = isAllDates ? formData.planning.length : aff.dates.length;

                        return (
                          <div key={aff.candidat_id} data-testid={`candidat-row-${aff.candidat_id}`}>
                            {/* Candidate header row */}
                            <div className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors">
                              <button
                                type="button"
                                onClick={() => setExpandedCandidatId(isExpanded ? null : aff.candidat_id)}
                                className="text-slate-400 hover:text-slate-600"
                              >
                                {isExpanded 
                                  ? <ChevronDown className="w-4 h-4" /> 
                                  : <ChevronRightIcon className="w-4 h-4" />
                                }
                              </button>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-slate-900">
                                  <CandidatLink dossierId={id} candidatId={candidat.id} icon>{candidat.nom} {candidat.prenom}</CandidatLink>
                                </p>
                                <p className="text-xs text-slate-500 truncate">{suiviCandidats[candidat.id]?.entreprise_finale ? <span className="text-indigo-700 font-medium" data-testid={`candidat-entreprise-${candidat.id}`}>{suiviCandidats[candidat.id].entreprise_finale}</span> : (candidat.entreprise || null)}{(suiviCandidats[candidat.id]?.entreprise_finale || candidat.entreprise) && candidat.email ? ' · ' : ''}{candidat.email}</p>
                              </div>
                              {suiviCandidats[candidat.id] && (
                                <div className="hidden md:flex items-center gap-4 text-xs" data-testid={`candidat-assiduite-${candidat.id}`}>
                                  {suiviCandidats[candidat.id].taux_presence != null && <SuiviKpi label="Présence" value={`${suiviCandidats[candidat.id].taux_presence} %`} warn={suiviCandidats[candidat.id].taux_presence < 80} />}
                                  <SuiviKpi label="Absences" value={suiviCandidats[candidat.id].nb_absences} warn={suiviCandidats[candidat.id].nb_absences > 0} />
                                  <SuiviKpi label="Retards" value={suiviCandidats[candidat.id].nb_retards} warn={suiviCandidats[candidat.id].nb_retards > 0} />
                                </div>
                              )}
                              {id && <CandidatFicheActions dossierId={id} candidatId={candidat.id} nom={candidat.nom} prenom={candidat.prenom} apiBase={`${API_URL}/portail/admin`} />}
                              <button
                                type="button"
                                onClick={() => setExpandedCandidatId(isExpanded ? null : aff.candidat_id)}
                                title="Parcours individualisé : cliquer pour choisir les dates suivies par ce candidat"
                                data-testid={`candidat-dates-badge-${aff.candidat_id}`}
                                className={cn(
                                  "text-xs px-2 py-0.5 rounded-full font-medium inline-flex items-center gap-1 hover:ring-2 hover:ring-offset-1 transition-shadow",
                                  isAllDates ? "bg-indigo-100 text-indigo-700 hover:ring-indigo-300" : "bg-amber-100 text-amber-700 hover:ring-amber-300"
                                )}
                              >
                                <CalendarDays className="w-3 h-3" />
                                {isAllDates ? `Toutes les dates (${nbDates})` : `Parcours individualisé · ${nbDates}/${formData.planning.length} date(s)`}
                              </button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveCandidat(aff.candidat_id)}
                                className="text-red-400 hover:text-red-600 hover:bg-red-50 h-8 w-8 p-0"
                                data-testid={`remove-candidat-${aff.candidat_id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>

                            {/* Expanded: date selection */}
                            {isExpanded && formData.planning.length > 0 && (
                              <div className="px-4 pb-3 pt-1 bg-slate-50/50 border-t">
                                <div className="flex items-center justify-between mb-2">
                                  <p className="text-xs font-medium text-slate-600">Parcours individualisé — dates suivies par ce candidat <span className="text-slate-400 font-normal">(cliquer sur une date pour l'inclure / l'exclure ; par défaut toutes les dates)</span></p>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleCandidatAllDates(aff.candidat_id)}
                                    className="text-xs h-6 px-2 text-indigo-600"
                                  >
                                    Toutes les dates
                                  </Button>
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                  {sortedPlanning
                                    .map((p) => {
                                      const isSelected = isAllDates || (aff.dates && aff.dates.includes(p.date));
                                      return (
                                        <button
                                          key={p.date}
                                          type="button"
                                          onClick={() => handleCandidatDateToggle(aff.candidat_id, p.date)}
                                          className={cn(
                                            "text-xs px-2 py-1 rounded-md border transition-colors capitalize",
                                            isSelected
                                              ? "bg-indigo-600 text-white border-indigo-600"
                                              : "bg-white text-slate-600 border-slate-200 hover:border-indigo-300"
                                          )}
                                        >
                                          {format(parseISO(p.date), 'EEE d MMM', { locale: fr })}
                                        </button>
                                      );
                                    })
                                  }
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Dialog: Nouveau candidat */}
            <Dialog open={candidatDialogOpen} onOpenChange={(open) => { setCandidatDialogOpen(open); if (!open) setDuplicatesFound([]); }}>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Nouveau candidat</DialogTitle>
                  <DialogDescription>Créer un candidat et l'affecter à ce dossier</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="grid grid-cols-[100px_1fr_1fr] gap-4">
                    <CiviliteSelect value={newCandidat.civilite} onChange={(v) => setNewCandidat({ ...newCandidat, civilite: v })} testId="new-candidat-civilite" />
                    <div className="space-y-2">
                      <Label>Prénom *</Label>
                      <Input
                        value={newCandidat.prenom}
                        onChange={(e) => { setNewCandidat({ ...newCandidat, prenom: formatPrenom(e.target.value) }); setDuplicatesFound([]); }}
                        data-testid="new-candidat-prenom"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Nom *</Label>
                      <Input
                        value={newCandidat.nom}
                        onChange={(e) => { setNewCandidat({ ...newCandidat, nom: formatNom(e.target.value) }); setDuplicatesFound([]); }}
                        data-testid="new-candidat-nom"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={newCandidat.email}
                      onChange={(e) => { setNewCandidat({ ...newCandidat, email: e.target.value }); setDuplicatesFound([]); }}
                      data-testid="new-candidat-email"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Téléphone</Label>
                      <Input
                        value={newCandidat.telephone}
                        onChange={(e) => setNewCandidat({ ...newCandidat, telephone: e.target.value })}
                        data-testid="new-candidat-telephone"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Date de naissance</Label>
                      <Input
                        type="date"
                        value={newCandidat.date_naissance}
                        onChange={(e) => setNewCandidat({ ...newCandidat, date_naissance: e.target.value })}
                        data-testid="new-candidat-date-naissance"
                      />
                    </div>
                  </div>

                  {/* Doublons détectés */}
                  {duplicatesFound.length > 0 && (
                    <div className="border border-amber-300 bg-amber-50 rounded-lg p-3 space-y-2" data-testid="duplicate-warning">
                      <div className="flex items-center gap-2 text-amber-700">
                        <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                        <p className="text-sm font-semibold">Doublon(s) détecté(s)</p>
                      </div>
                      <p className="text-xs text-amber-600">Un ou plusieurs candidats similaires existent déjà. Vous pouvez utiliser l'existant ou forcer la création.</p>
                      <div className="space-y-1.5 mt-2">
                        {duplicatesFound.map(dup => (
                          <div key={dup.id} className="flex items-center justify-between bg-white border border-amber-200 rounded-md px-3 py-2">
                            <div>
                              <span className="text-sm font-medium text-slate-800">{dup.nom} {dup.prenom}</span>
                              <span className="text-xs text-slate-500 ml-2">{dup.email}</span>
                              {dup.telephone && <span className="text-xs text-slate-400 ml-2">{dup.telephone}</span>}
                            </div>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="text-xs border-indigo-300 text-indigo-600 hover:bg-indigo-50"
                              onClick={() => handleUseDuplicate(dup)}
                              data-testid={`use-duplicate-${dup.id}`}
                            >
                              <Check className="w-3 h-3 mr-1" />
                              Utiliser celui-ci
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <DialogFooter className="flex gap-2">
                  <Button type="button" variant="outline" onClick={() => { setCandidatDialogOpen(false); setDuplicatesFound([]); }}>Annuler</Button>
                  {duplicatesFound.length > 0 ? (
                    <Button
                      type="button"
                      onClick={handleForceCreateCandidat}
                      disabled={creatingCandidat}
                      className="bg-amber-600 hover:bg-amber-700"
                      data-testid="force-create-candidat"
                    >
                      {creatingCandidat ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <AlertTriangle className="w-4 h-4 mr-2" />}
                      Créer quand même
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      onClick={handleCheckAndCreateCandidat}
                      disabled={creatingCandidat}
                      className="bg-indigo-600 hover:bg-indigo-700"
                      data-testid="create-candidat-submit"
                    >
                      {creatingCandidat ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                      Créer et ajouter
                    </Button>
                  )}
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Dialog: Import candidats confirmation */}
            <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
              <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
                <DialogHeader>
                  <DialogTitle className="font-['Plus_Jakarta_Sans'] flex items-center gap-2">
                    <Upload className="w-5 h-5 text-indigo-600" />
                    Importer des candidats
                  </DialogTitle>
                  <DialogDescription>
                    {importParsed.length} candidat(s) détecté(s) — {importParsed.filter(p => p.is_duplicate).length} doublon(s) trouvé(s)
                    {importParsed.some(p => p.email_invalid) && (
                      <span className="block text-orange-600 mt-1" data-testid="import-invalid-warning">
                        {importParsed.filter(p => p.email_invalid).length} email(s) invalide(s) : corrigez-les ou videz le champ pour importer.
                      </span>
                    )}
                  </DialogDescription>
                </DialogHeader>
                <div className="flex-1 overflow-y-auto border rounded-lg">
                  <div className="grid grid-cols-[40px_50px_1fr_1fr_1fr_1fr_80px] gap-2 bg-slate-100 border-b px-3 py-2 text-[10px] font-medium text-slate-500 uppercase sticky top-0">
                    <span></span><span>Civ.</span><span>Nom</span><span>Prénom</span><span>Email</span><span>Téléphone</span><span>Statut</span>
                  </div>
                  <div className="divide-y">
                    {importParsed.map((p, idx) => (
                      <div key={p._uid} className={cn(
                        "grid grid-cols-[40px_50px_1fr_1fr_1fr_1fr_80px] gap-2 items-center px-3 py-2 text-sm",
                        p.is_duplicate ? "bg-amber-50" : "bg-white",
                        !p.selected && "opacity-40"
                      )} data-testid={`import-row-${idx}`}>
                        <Checkbox
                          checked={p.selected}
                          onCheckedChange={(checked) => {
                            setImportParsed(prev => prev.map((item, i) => i === idx ? { ...item, selected: !!checked } : item));
                          }}
                          data-testid={`import-check-${idx}`}
                        />
                        <select
                          value={p.civilite || ''}
                          onChange={(e) => setImportParsed(prev => prev.map((item, i) => i === idx ? { ...item, civilite: e.target.value || null } : item))}
                          className="h-6 text-xs rounded border border-slate-200 bg-white px-1"
                          data-testid={`import-civilite-${idx}`}
                        >
                          <option value="">—</option>
                          <option value="M.">M.</option>
                          <option value="Mme">Mme</option>
                        </select>
                        <span className="text-xs text-slate-900 truncate">{p.nom}</span>
                        <span className="text-xs text-slate-900 truncate">{p.prenom}</span>
                        <input
                          value={p.email || ''}
                          onChange={(e) => {
                            const email = e.target.value.replace(/\s+/g, '');
                            setImportParsed(prev => prev.map((item, i) => i === idx ? { ...item, email, email_invalid: !isEmailValid(email) } : item));
                          }}
                          className={cn("h-6 text-xs rounded border bg-white px-1 min-w-0 w-full", p.email_invalid ? "border-orange-400 text-orange-700" : "border-slate-200 text-slate-600")}
                          placeholder="—"
                          data-testid={`import-email-${idx}`}
                        />
                        <span className="text-xs text-slate-500 truncate">{p.telephone || '—'}</span>
                        {p.email_invalid ? (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 font-medium text-center" title="Corrigez ou videz l'email" data-testid={`import-status-invalid-${idx}`}>Email invalide</span>
                        ) : p.is_duplicate ? (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-medium text-center" title={`Existe déjà : ${p.existing_nom}`}>Doublon</span>
                        ) : (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-700 font-medium text-center">Nouveau</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex items-center justify-between pt-2 text-xs text-slate-500">
                  <div className="flex items-center gap-3">
                    <button type="button" onClick={() => setImportParsed(prev => prev.map(p => ({ ...p, selected: true })))} className="text-indigo-600 hover:underline">Tout sélectionner</button>
                    <button type="button" onClick={() => setImportParsed(prev => prev.map(p => ({ ...p, selected: false })))} className="text-slate-500 hover:underline">Tout désélectionner</button>
                    <button type="button" onClick={() => setImportParsed(prev => prev.map(p => ({ ...p, selected: !p.is_duplicate })))} className="text-amber-600 hover:underline">Exclure les doublons</button>
                  </div>
                  <span>{importParsed.filter(p => p.selected).length} sélectionné(s)</span>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => { setImportDialogOpen(false); setImportParsed([]); }}>
                    Annuler
                  </Button>
                  <Button
                    type="button"
                    className="bg-indigo-600 hover:bg-indigo-700"
                    onClick={handleConfirmImport}
                    disabled={importConfirming || importParsed.filter(p => p.selected).length === 0 || importParsed.some(p => p.selected && p.email_invalid)}
                    data-testid="confirm-import-btn"
                  >
                    {importConfirming ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Import en cours...</>
                    ) : (
                      <><Plus className="w-4 h-4 mr-2" />Importer {importParsed.filter(p => p.selected).length} candidat(s)</>
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Planning Tab */}
            <TabsContent value="planning">
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg">Planning des séances</CardTitle>
                  <p className="text-sm text-slate-500 mt-1">
                    Cliquez sur une date pour l'ajouter ou la retirer. <strong>Shift+clic</strong> pour sélectionner toute une plage (jours ouvrés uniquement, hors week-ends). Vous pouvez ensuite cliquer manuellement sur un samedi pour l'ajouter.
                  </p>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-6">
                    {/* Calendar for selecting dates */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <Label className="text-base font-medium">Sélectionner les dates de formation</Label>
                      </div>
                      
                      {/* Quick selection buttons */}
                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setRangeDialogOpen(true)}
                          className="text-xs bg-indigo-50 border-indigo-200 text-indigo-700 hover:bg-indigo-100"
                          data-testid="btn-select-range"
                        >
                          <CalendarRange className="w-3 h-3 mr-1" />
                          Sélectionner une période
                        </Button>
                        <label
                          className="inline-flex items-center h-8 px-3 rounded-md border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 text-xs font-medium cursor-pointer transition-colors"
                          data-testid="import-dates-btn"
                        >
                          <Upload className="w-3 h-3 mr-1" />
                          Importer dates (Excel)
                          <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleImportDates} data-testid="import-dates-input" />
                        </label>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            // Add current week's workdays (Mon-Fri)
                            const today = new Date();
                            const monday = new Date(today);
                            monday.setDate(today.getDate() - today.getDay() + 1);
                            
                            const newDates = [];
                            for (let i = 0; i < 5; i++) {
                              const date = new Date(monday);
                              date.setDate(monday.getDate() + i);
                              const dateStr = format(date, 'yyyy-MM-dd');
                              if (!formData.planning.find(p => p.date === dateStr) && !isJourFerie(dateStr)) {
                                newDates.push({
                                  date: dateStr,
                                  demi_journee: 'journee',
                                  formateur_id: '',
                                  module_id: ''
                                });
                              }
                            }
                            if (newDates.length > 0) {
                              setFormData({ ...formData, planning: [...formData.planning, ...newDates].sort((a, b) => a.date.localeCompare(b.date)) });
                              toast.success(`${newDates.length} jour(s) ajouté(s)`);
                            } else {
                              toast.info('Ces dates sont déjà sélectionnées');
                            }
                          }}
                          className="text-xs"
                        >
                          + Semaine en cours
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            // Add next week's workdays
                            const today = new Date();
                            const nextMonday = new Date(today);
                            nextMonday.setDate(today.getDate() - today.getDay() + 8);
                            
                            const newDates = [];
                            for (let i = 0; i < 5; i++) {
                              const date = new Date(nextMonday);
                              date.setDate(nextMonday.getDate() + i);
                              const dateStr = format(date, 'yyyy-MM-dd');
                              if (!formData.planning.find(p => p.date === dateStr) && !isJourFerie(dateStr)) {
                                newDates.push({
                                  date: dateStr,
                                  demi_journee: 'journee',
                                  formateur_id: '',
                                  module_id: ''
                                });
                              }
                            }
                            if (newDates.length > 0) {
                              setFormData({ ...formData, planning: [...formData.planning, ...newDates].sort((a, b) => a.date.localeCompare(b.date)) });
                              toast.success(`${newDates.length} jour(s) ajouté(s)`);
                            } else {
                              toast.info('Ces dates sont déjà sélectionnées');
                            }
                          }}
                          className="text-xs"
                        >
                          + Semaine prochaine
                        </Button>
                        {formData.planning.length > 0 && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setFormData({ ...formData, planning: [] })}
                            className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            Tout effacer
                          </Button>
                        )}
                      </div>

                      <div className="border rounded-lg p-3 bg-white w-full overflow-x-auto" data-testid="planning-calendar-container">
                        <Calendar
                          selected={formData.planning.map(p => parseISO(p.date))}
                          onDayClick={(day, modifiers, e) => {
                            const dateStr = format(day, 'yyyy-MM-dd');
                            const isAlreadySelected = formData.planning.some(p => p.date === dateStr);
                            
                            if (e.shiftKey && lastClickedDate) {
                              // Shift+click: add all weekdays between lastClickedDate and this date
                              const start = lastClickedDate < day ? lastClickedDate : day;
                              const end = lastClickedDate < day ? day : lastClickedDate;
                              const current = new Date(start);
                              const newDates = [];
                              
                              while (current <= end) {
                                const dayOfWeek = current.getDay();
                                const curStr = format(current, 'yyyy-MM-dd');
                                // Exclude only Sunday (0) and Saturday (6) and jours fériés for range selection
                                if (dayOfWeek !== 0 && dayOfWeek !== 6 && !isJourFerie(curStr)) {
                                  if (!formData.planning.some(p => p.date === curStr)) {
                                    newDates.push({
                                      date: curStr,
                                      demi_journee: 'journee',
                                      formateur_id: '',
                                      module_id: ''
                                    });
                                  }
                                }
                                current.setDate(current.getDate() + 1);
                              }
                              
                              if (newDates.length > 0) {
                                setFormData({
                                  ...formData,
                                  planning: [...formData.planning, ...newDates].sort((a, b) => a.date.localeCompare(b.date))
                                });
                                toast.success(`${newDates.length} jour(s) ouvré(s) ajouté(s)`);
                              } else {
                                toast.info('Toutes les dates ouvrées de cette plage sont déjà sélectionnées');
                              }
                              setLastClickedDate(day);
                            } else {
                              // Normal click: toggle the individual date (any day, including Saturday)
                              if (isAlreadySelected) {
                                setFormData({
                                  ...formData,
                                  planning: formData.planning.filter(p => p.date !== dateStr)
                                });
                              } else {
                                const newEntry = {
                                  date: dateStr,
                                  demi_journee: 'journee',
                                  formateur_id: '',
                                  module_id: ''
                                };
                                setFormData({
                                  ...formData,
                                  planning: [...formData.planning, newEntry].sort((a, b) => a.date.localeCompare(b.date))
                                });
                              }
                              setLastClickedDate(day);
                            }
                          }}
                          locale={fr}
                          numberOfMonths={4}
                          modifiers={{ ferie: (date) => isJourFerieDate(date) }}
                          modifiersClassNames={{ ferie: 'day-ferie' }}
                          components={{
                            DayContent: ({ date }) => {
                              const label = getJourFerieLabel(format(date, 'yyyy-MM-dd'));
                              return <span title={label || undefined} data-testid={label ? `ferie-${format(date, 'yyyy-MM-dd')}` : undefined}>{date.getDate()}</span>;
                            }
                          }}
                          className="rounded-md"
                          classNames={{
                            months: "flex flex-col lg:flex-row flex-wrap gap-2",
                            month: "space-y-2",
                            caption: "flex justify-center pt-1 relative items-center text-sm",
                            table: "w-full border-collapse",
                            head_cell: "text-slate-500 font-normal text-xs w-8",
                            cell: "text-center text-sm p-0 relative",
                            day: "h-8 w-8 p-0 font-normal text-sm",
                            day_selected: "bg-indigo-600 text-white hover:bg-indigo-700 rounded-md",
                            day_today: "bg-slate-100 text-slate-900 rounded-md",
                            day_range_middle: "bg-indigo-100"
                          }}
                        />
                      </div>
                      <p className="text-sm text-slate-600">
                        <strong>{formData.planning.length}</strong> jour(s) sélectionné(s)
                        <span className="text-xs text-slate-400 ml-2">• Clic = ajouter/retirer un jour • Shift+clic = sélectionner une plage (jours ouvrés)</span>
                        <span className="inline-flex items-center gap-1 text-xs text-slate-400 ml-2" data-testid="legende-ferie"><span className="inline-block w-3 h-3 rounded-sm bg-rose-100 border border-rose-300" /> Jour férié</span>
                      </p>
                    </div>

                    {/* Périodes de stage (PAE) */}
                    {formData.planning.length > 0 && (
                      <div className="border border-violet-200 rounded-lg bg-violet-50/50 p-4 space-y-3" data-testid="pae-section">
                        <div className="flex items-center justify-between">
                          <Label className="text-sm font-semibold text-violet-700 flex items-center gap-2">
                            <Briefcase className="w-4 h-4" />
                            Périodes d'Application en Entreprise (PAE)
                          </Label>
                          {(formData.periodes_stage || []).length < 3 && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="text-xs text-violet-600 border-violet-300 hover:bg-violet-100"
                              data-testid="add-pae-btn"
                              onClick={() => {
                                const nextNum = (formData.periodes_stage || []).length + 1;
                                setFormData({
                                  ...formData,
                                  periodes_stage: [...(formData.periodes_stage || []), { label: `PAE ${nextNum}`, date_debut: '', date_fin: '', _uid: crypto.randomUUID ? crypto.randomUUID() : `${Math.random()}` }]
                                });
                              }}
                            >
                              <Plus className="w-3 h-3 mr-1" /> Ajouter une PAE
                            </Button>
                          )}
                        </div>

                        {(!formData.periodes_stage || formData.periodes_stage.length === 0) && (
                          <p className="text-xs text-violet-400 italic">Aucune période de stage définie. Cliquez sur "Ajouter une PAE" pour déclarer une période.</p>
                        )}

                        {(formData.periodes_stage || []).map((pae, idx) => (
                          <div key={pae._uid || pae.label} className="flex items-center gap-3 bg-white rounded-md border border-violet-200 px-3 py-2" data-testid={`pae-row-${idx}`}>
                            <span className="text-xs font-bold text-violet-700 bg-violet-100 px-2 py-1 rounded min-w-[50px] text-center">{pae.label}</span>
                            <div className="flex items-center gap-2 flex-1">
                              <Label className="text-xs text-slate-500 whitespace-nowrap">Du</Label>
                              <Input
                                type="date"
                                value={pae.date_debut || ''}
                                onChange={(e) => {
                                  const newPeriodes = [...(formData.periodes_stage || [])];
                                  newPeriodes[idx] = { ...newPeriodes[idx], date_debut: e.target.value };
                                  setFormData({ ...formData, periodes_stage: newPeriodes });
                                }}
                                className="h-7 text-xs w-40"
                                data-testid={`pae-debut-${idx}`}
                              />
                              <Label className="text-xs text-slate-500 whitespace-nowrap">au</Label>
                              <Input
                                type="date"
                                value={pae.date_fin || ''}
                                onChange={(e) => {
                                  const newPeriodes = [...(formData.periodes_stage || [])];
                                  newPeriodes[idx] = { ...newPeriodes[idx], date_fin: e.target.value };
                                  setFormData({ ...formData, periodes_stage: newPeriodes });
                                }}
                                className="h-7 text-xs w-40"
                                data-testid={`pae-fin-${idx}`}
                              />
                            </div>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="text-xs text-violet-600 border-violet-300 hover:bg-violet-100"
                              data-testid={`pae-apply-${idx}`}
                              onClick={() => {
                                if (!pae.date_debut || !pae.date_fin) {
                                  toast.error('Veuillez saisir les dates de début et fin');
                                  return;
                                }
                                let count = 0;
                                const newPlanning = formData.planning.map(p => {
                                  if (p.date >= pae.date_debut && p.date <= pae.date_fin) {
                                    count++;
                                    return { ...p, type_journee: 'stage', organisme_externe: pae.label };
                                  }
                                  return p;
                                });
                                setFormData({ ...formData, planning: newPlanning });
                                if (count > 0) toast.success(`${count} jour(s) marqué(s) en ${pae.label}`);
                                else toast.info('Aucun jour du planning ne correspond à cette période');
                              }}
                            >
                              Appliquer
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="text-red-400 hover:text-red-600 hover:bg-red-50 h-7 w-7 p-0"
                              onClick={() => {
                                const removedLabel = pae.label;
                                const newPeriodes = (formData.periodes_stage || []).filter((_, i) => i !== idx);
                                const newPlanning = formData.planning.map(p =>
                                  p.organisme_externe === removedLabel && p.type_journee === 'stage'
                                    ? { ...p, type_journee: 'formation', organisme_externe: '' }
                                    : p
                                );
                                setFormData({ ...formData, periodes_stage: newPeriodes, planning: newPlanning });
                                toast.info(`${removedLabel} supprimée, jours remis en formation`);
                              }}
                            >
                              <X className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* List of selected dates - Visual read-only cards */}
                    <div className="space-y-3">
                      <Label className="text-base font-medium">Détails des séances</Label>
                      {formData.planning.length === 0 ? (
                        <div className="border-2 border-dashed rounded-lg p-6 text-center">
                          <CalendarIcon className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                          <p className="text-slate-500 text-sm">Sélectionnez des dates dans le calendrier</p>
                        </div>
                      ) : (
                        <div className="space-y-1 max-h-[450px] overflow-y-auto pr-1">
                          {sortedPlanning
                            .map((aff) => {
                              const dayOfWeek = parseISO(aff.date).getDay();
                              const isSaturday = dayOfWeek === 6;
                              const isStage = aff.type_journee === 'stage';
                              const isExterne = aff.type_journee === 'prestation_externe';
                              const formateurObj = formateurs.find(f => f.id === aff.formateur_id);
                              const moduleObj = modules.find(m => m.id === aff.module_id);
                              const dayModules = (Array.isArray(aff.modules_pedagogiques) && aff.modules_pedagogiques.length > 0)
                                ? aff.modules_pedagogiques
                                    .map(mp => modules.find(m => m.id === mp.module_id))
                                    .filter(Boolean)
                                : (moduleObj ? [moduleObj] : []);
                              const lieuName = aff.lieu || '';
                              const typeBg = isStage ? 'border-l-violet-500 bg-violet-50/40' : isExterne ? 'border-l-orange-500 bg-orange-50/40' : isSaturday ? 'border-l-amber-500 bg-amber-50/40' : 'border-l-indigo-500 bg-white';
                              const typeLabel = isStage ? 'Stage' : isExterne ? 'Presta. ext.' : 'Formation';
                              const typeColor = isStage ? 'bg-violet-100 text-violet-700' : isExterne ? 'bg-orange-100 text-orange-700' : 'bg-indigo-100 text-indigo-700';
                              const periodeLabel = aff.demi_journee === 'matin' ? 'Matin' : aff.demi_journee === 'apres_midi' ? 'Après-midi' : 'Journée';

                              return (
                                <div
                                  key={aff.date}
                                  onClick={() => {
                                    if (aff.formateur_id) fetchDisponibilite(aff.date);
                                    setEditingDay({ ...aff });
                                  }}
                                  className={`flex items-center gap-3 px-3 py-2 rounded-lg border border-l-4 cursor-pointer transition-all hover:shadow-sm hover:ring-1 hover:ring-indigo-200 ${typeBg}`}
                                  data-testid={`planning-row-${aff.date}`}
                                >
                                  {/* Date */}
                                  <div className="w-[90px] shrink-0">
                                    <p className={`text-xs font-bold capitalize ${isStage ? 'text-violet-700' : isExterne ? 'text-orange-700' : isSaturday ? 'text-amber-700' : 'text-indigo-700'}`}>
                                      {format(parseISO(aff.date), 'EEE d MMM', { locale: fr })}
                                    </p>
                                  </div>

                                  {/* Type + Période badges */}
                                  <div className="flex items-center gap-1.5 min-w-[140px] shrink-0">
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${typeColor}`}>{typeLabel}</span>
                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-medium" data-testid={`planning-periode-${aff.date}`}>{periodeLabel}{!aff.formateur_id && !isStage && !isExterne ? <span className="text-slate-400 font-normal"> · {horairesLabel(horaires, aff.demi_journee)}</span> : null}</span>
                                  </div>

                                  {/* Formateur */}
                                  <div className="flex-1 min-w-0">
                                    {formateurObj ? (
                                      <span className="text-xs text-slate-700">{formateurObj.nom} {formateurObj.prenom}</span>
                                    ) : (
                                      <span className="text-xs text-slate-300 italic">Formateur non assigné</span>
                                    )}
                                  </div>

                                  {/* Module */}
                                  <div className="flex-1 min-w-0">
                                    {dayModules.length > 0 ? (
                                      <span className="text-xs text-slate-600 truncate block" title={dayModules.map(m => m.titre).join(' · ')}>
                                        {dayModules.map((m, i) => (
                                          <span key={m.id}>
                                            {i > 0 && <span className="text-slate-300"> · </span>}
                                            <span className="font-mono text-[10px] text-indigo-500">{m.code}</span> {m.titre}
                                          </span>
                                        ))}
                                      </span>
                                    ) : (
                                      <span className="text-xs text-slate-300 italic">Module non défini</span>
                                    )}
                                  </div>

                                  {/* Cahier status */}
                                  <div className="w-[60px] shrink-0 flex justify-center">
                                    {aff.presences_validees ? (
                                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold" title="Présences validées">Validé</span>
                                    ) : aff.has_cahier ? (
                                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold" title="Cahier en cours">En cours</span>
                                    ) : null}
                                  </div>

                                  {/* Lieu */}
                                  <div className="w-[100px] shrink-0 text-right">
                                    {lieuName ? (
                                      <span className="text-[10px] text-slate-500">{lieuName}</span>
                                    ) : isExterne && aff.organisme_externe ? (
                                      <span className="text-[10px] text-orange-500">{aff.organisme_externe}</span>
                                    ) : isStage && aff.organisme_externe ? (
                                      <span className="text-[10px] text-violet-500 font-medium">{aff.organisme_externe}</span>
                                    ) : null}
                                  </div>

                                  {/* Delete (stop propagation) */}
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const newPlanning = formData.planning.filter(p => p.date !== aff.date);
                                      setFormData({ ...formData, planning: newPlanning });
                                    }}
                                    className="text-red-300 hover:text-red-600 hover:bg-red-50 h-7 w-7 p-0 shrink-0"
                                  >
                                    <X className="w-3.5 h-3.5" />
                                  </Button>
                                </div>
                              );
                            })}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Planning Pédagogique Tab */}
            {isEdit && formData.planning.length > 0 && formData.formation_id && (
              <TabsContent value="planning_pedagogique">
                <PlanningPedagogiqueTab
                  planning={formData.planning}
                  formations={formations}
                  formationId={formData.formation_id}
                  modules={modules}
                  formateurs={formateurs}
                  lieux={lieux}
                  dossierId={id}
                  token={getAuthToken()}
                  referentId={formData.referent_id}
                  lieuPrincipal={formData.lieu_principal}
                  candidatsAffectes={candidats.filter(c => (formData.candidats || []).some(fc => fc.candidat_id === c.id))}
                  onPlanningChange={(newPlanning) => setFormData({ ...formData, planning: newPlanning })}
                  canUnlock={user?.role === 'admin' || user?.role === 'super_admin'}
                  deverrouilles={deverrouilles}
                  onDeverrouillesChange={setDeverrouilles}
                />
              </TabsContent>
            )}

            {/* Fil chronologique Tab */}
            {isEdit && (
              <TabsContent value="fil_chronologique">
                <FilChronologiqueTab dossierId={id} />
              </TabsContent>
            )}

            {/* Rapports Tab */}
            {isEdit && (
              <TabsContent value="rapports">
                <RapportDossierPanel dossierId={id} />
              </TabsContent>
            )}
            {isEdit && (
              <TabsContent value="bilan">
                <RapportDossierPanel dossierId={id} initialVue="bilan" />
              </TabsContent>
            )}

            {/* Financement Tab (Phase 2) */}
            {isEdit && (
              <TabsContent value="financement">
                <FinancementTab
                  dossierId={id}
                  formData={formData}
                  financeurs={financeurs}
                  formations={formations}
                  modules={modules}
                  clients={clients}
                  onDfsChanged={refreshDfSummary}
                  onFinanceursUpdated={async () => {
                    try {
                      const res = await axios.get(`${API_URL}/financeurs`, { headers });
                      setFinanceurs(res.data);
                    } catch {}
                  }}
                />
              </TabsContent>
            )}

            {/* Facturation Tab (Phase 4) — masqué au responsable pédagogique */}
            {isEdit && voirCouts && (
              <TabsContent value="facturation">
                <FacturationTab
                  dossierId={id}
                  formData={formData}
                  financeurs={financeurs}
                />
              </TabsContent>
            )}

            {/* Workflow Tab */}
            {isEdit && (
              <TabsContent value="workflow">
                <div className="space-y-6" data-testid="workflow-tab-content">
                  <WorkflowPanel
                    actionFormationId={id}
                    typeWorkflow="pedagogique"
                    formateurs={formateurs}
                  />
                  <WorkflowPanel
                    actionFormationId={id}
                    typeWorkflow="administratif"
                    formateurs={formateurs}
                  />
                </div>
              </TabsContent>
            )}
          </Tabs>

          {/* Submit Button */}
          <div className="flex justify-end gap-4 pt-6">
            <Button type="button" variant="outline" onClick={() => navigate(isEdit ? `/dossiers/${id}` : '/dossiers')} data-testid="btn-annuler-dossier">
              Annuler
            </Button>
            <Button 
              type="submit" 
              className="bg-indigo-600 hover:bg-indigo-700"
              disabled={loading || isLocked}
              data-testid="submit-dossier"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enregistrement...
                </>
              ) : isLocked ? (
                <>
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  Action verrouillée
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {isEdit ? 'Mettre à jour' : 'Créer l\'action'}
                </>
              )}
            </Button>
          </div>
        </form>

        {/* Dialogue de confirmation de suppression */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="text-red-600 flex items-center gap-2">
                <Trash2 className="w-5 h-5" /> Supprimer l'action de {formData.type_dossier === 'aft' ? 'certification' : 'formation'}
              </DialogTitle>
              <DialogDescription>
                Cette action est irréversible. Toutes les données liées seront supprimées (planning, candidats, dossiers financeurs, documents).
              </DialogDescription>
            </DialogHeader>
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm font-semibold text-red-700">{formData.code} — {formData.intitule}</p>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Annuler</Button>
              <Button variant="destructive" onClick={handleDeleteAction} data-testid="confirm-delete-action">
                <Trash2 className="w-4 h-4 mr-2" /> Confirmer la suppression
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog for creating new formation */}
        <Dialog open={formationDialogOpen} onOpenChange={setFormationDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-['Plus_Jakarta_Sans']">
                Nouvelle formation
              </DialogTitle>
              <DialogDescription>
                Créez une nouvelle formation qui sera automatiquement sélectionnée pour ce dossier.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="new-formation-code">Code *</Label>
                  <Input
                    id="new-formation-code"
                    value={newFormation.code}
                    onChange={(e) => setNewFormation({ ...newFormation, code: e.target.value })}
                    placeholder="Ex: FORM-001"
                    data-testid="input-new-formation-code"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-formation-type">Type</Label>
                  <Select
                    value={newFormation.type_formation}
                    onValueChange={(value) => setNewFormation({ ...newFormation, type_formation: value })}
                  >
                    <SelectTrigger data-testid="select-new-formation-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TYPES_FORMATION.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="new-formation-titre">Titre *</Label>
                <Input
                  id="new-formation-titre"
                  value={newFormation.titre}
                  onChange={(e) => setNewFormation({ ...newFormation, titre: e.target.value })}
                  placeholder="Ex: Formation Excel Avancé"
                  data-testid="input-new-formation-titre"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="new-formation-duree">Durée totale (heures)</Label>
                <Input
                  id="new-formation-duree"
                  type="number"
                  value={newFormation.duree_totale_heures}
                  onChange={(e) => setNewFormation({ ...newFormation, duree_totale_heures: parseInt(e.target.value) || 0 })}
                  min="0"
                  data-testid="input-new-formation-duree"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setFormationDialogOpen(false)}>
                Annuler
              </Button>
              <Button 
                type="button" 
                className="bg-indigo-600 hover:bg-indigo-700"
                onClick={handleCreateFormation}
                disabled={creatingFormation}
                data-testid="submit-new-formation"
              >
                {creatingFormation ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Création...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Créer et sélectionner
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Client Dialog */}
        <Dialog open={clientDialogOpen} onOpenChange={setClientDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-['Plus_Jakarta_Sans'] flex items-center gap-2">
                <Building2 className="w-5 h-5 text-amber-600" />
                Nouveau client
              </DialogTitle>
              <DialogDescription>
                Créez un nouveau client qui sera automatiquement sélectionné
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="new-client-nom">Nom de l'entreprise *</Label>
                <Input
                  id="new-client-nom"
                  value={newClient.nom}
                  onChange={(e) => setNewClient({ ...newClient, nom: e.target.value })}
                  placeholder="Ex: Entreprise ABC"
                  data-testid="input-new-client-nom"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="new-client-email">Email</Label>
                  <Input
                    id="new-client-email"
                    type="email"
                    value={newClient.email}
                    onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                    placeholder="contact@entreprise.fr"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-client-telephone">Téléphone</Label>
                  <Input
                    id="new-client-telephone"
                    value={newClient.telephone}
                    onChange={(e) => setNewClient({ ...newClient, telephone: e.target.value })}
                    placeholder="01 23 45 67 89"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-client-ville">Ville</Label>
                <Input
                  id="new-client-ville"
                  value={newClient.ville}
                  onChange={(e) => setNewClient({ ...newClient, ville: e.target.value })}
                  placeholder="Ex: Paris"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setClientDialogOpen(false)}>
                Annuler
              </Button>
              <Button 
                type="button" 
                className="bg-amber-600 hover:bg-amber-700"
                onClick={handleCreateClient}
                disabled={creatingClient}
                data-testid="submit-new-client"
              >
                {creatingClient ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Création...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Créer et sélectionner
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Financeur Dialog */}
        <Dialog open={financeurDialogOpen} onOpenChange={setFinanceurDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-['Plus_Jakarta_Sans'] flex items-center gap-2">
                <Landmark className="w-5 h-5 text-blue-600" />
                Nouveau financeur
              </DialogTitle>
              <DialogDescription>
                Créez un nouveau financeur qui sera automatiquement sélectionné
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="new-financeur-nom">Nom *</Label>
                  <Input
                    id="new-financeur-nom"
                    value={newFinanceur.nom}
                    onChange={(e) => setNewFinanceur({ ...newFinanceur, nom: e.target.value })}
                    placeholder="Ex: OPCO Atlas"
                    data-testid="input-new-financeur-nom"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-financeur-type">Type</Label>
                  <Select
                    value={newFinanceur.type_financeur}
                    onValueChange={(value) => setNewFinanceur({ ...newFinanceur, type_financeur: value })}
                  >
                    <SelectTrigger data-testid="select-new-financeur-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TYPES_FINANCEUR.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="new-financeur-email">Email</Label>
                  <Input
                    id="new-financeur-email"
                    type="email"
                    value={newFinanceur.email}
                    onChange={(e) => setNewFinanceur({ ...newFinanceur, email: e.target.value })}
                    placeholder="contact@opco.fr"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-financeur-telephone">Téléphone</Label>
                  <Input
                    id="new-financeur-telephone"
                    value={newFinanceur.telephone}
                    onChange={(e) => setNewFinanceur({ ...newFinanceur, telephone: e.target.value })}
                    placeholder="01 23 45 67 89"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setFinanceurDialogOpen(false)}>
                Annuler
              </Button>
              <Button 
                type="button" 
                className="bg-blue-600 hover:bg-blue-700"
                onClick={handleCreateFinanceur}
                disabled={creatingFinanceur}
                data-testid="submit-new-financeur"
              >
                {creatingFinanceur ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Création...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Créer et sélectionner
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog Nouveau Lieu */}
        <Dialog open={afLieeDialogOpen} onOpenChange={setAfLieeDialogOpen}>
          <DialogContent className="max-w-md" data-testid="dialog-create-af-liee">
            <DialogHeader>
              <DialogTitle>Créer une action de formation à rattacher</DialogTitle>
              <DialogDescription>
                L'action sera créée avec un code automatique puis rattachée à cette AF. Vous pourrez compléter sa fiche (formation, planning, candidats) ensuite.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="space-y-2">
                <Label>Formation *</Label>
                <FormationPicker
                  formations={formations}
                  value={newAfLiee.formation_id}
                  testId="select-new-af-formation"
                  placeholder="Sélectionner une formation..."
                  onChange={(v, f) => {
                    const oldF = formations.find(fo => fo.id === newAfLiee.formation_id);
                    setNewAfLiee(prev => ({
                      ...prev,
                      formation_id: v,
                      intitule: f && (!prev.intitule.trim() || (oldF && prev.intitule === oldF.titre)) ? f.titre : prev.intitule,
                    }));
                  }}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-af-titre">Intitulé de l'action *</Label>
                <Input
                  id="new-af-titre"
                  value={newAfLiee.intitule}
                  onChange={(e) => setNewAfLiee({ ...newAfLiee, intitule: e.target.value })}
                  placeholder="Choisissez d'abord une formation, l'intitulé se remplira automatiquement"
                  data-testid="input-new-af-titre"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-af-date">Date de début *</Label>
                <Input
                  id="new-af-date"
                  type="date"
                  value={newAfLiee.date_debut}
                  onChange={(e) => setNewAfLiee({ ...newAfLiee, date_debut: e.target.value })}
                  data-testid="input-new-af-date"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAfLieeDialogOpen(false)}>Annuler</Button>
              <Button type="button" onClick={handleCreateAfLiee} disabled={creatingAfLiee} className="bg-indigo-600 hover:bg-indigo-700" data-testid="btn-confirm-create-af-liee">
                {creatingAfLiee ? 'Création...' : 'Créer et rattacher'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Rattachement express après création d'une AF liée */}

        <Dialog open={lieuDialogOpen} onOpenChange={setLieuDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-['Plus_Jakarta_Sans'] flex items-center gap-2">
                <Building2 className="w-5 h-5 text-emerald-600" />
                Nouveau lieu
              </DialogTitle>
              <DialogDescription>
                Créez un nouveau lieu qui sera automatiquement sélectionné
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="new-lieu-nom">Nom du lieu *</Label>
                <Input
                  id="new-lieu-nom"
                  value={newLieu.nom}
                  onChange={(e) => setNewLieu({ ...newLieu, nom: e.target.value })}
                  placeholder="Ex: Salle A - Centre Paris"
                  data-testid="input-new-lieu-nom"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-lieu-adresse">Adresse</Label>
                <Input
                  id="new-lieu-adresse"
                  value={newLieu.adresse}
                  onChange={(e) => setNewLieu({ ...newLieu, adresse: e.target.value })}
                  placeholder="12 rue de la Formation"
                  data-testid="input-new-lieu-adresse"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="new-lieu-ville">Ville</Label>
                  <Input
                    id="new-lieu-ville"
                    value={newLieu.ville}
                    onChange={(e) => setNewLieu({ ...newLieu, ville: e.target.value })}
                    placeholder="Paris"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-lieu-cp">Code postal</Label>
                  <Input
                    id="new-lieu-cp"
                    value={newLieu.code_postal}
                    onChange={(e) => setNewLieu({ ...newLieu, code_postal: e.target.value })}
                    placeholder="75001"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setLieuDialogOpen(false)}>
                Annuler
              </Button>
              <Button 
                type="button" 
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={handleCreateLieu}
                disabled={creatingLieu}
                data-testid="submit-new-lieu"
              >
                {creatingLieu ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Création...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Créer et sélectionner
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Day Dialog */}
        <Dialog open={!!editingDay} onOpenChange={(open) => { if (!open) setEditingDay(null); }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CalendarIcon className="w-5 h-5 text-indigo-600" />
                {editingDay && format(parseISO(editingDay.date), 'EEEE d MMMM yyyy', { locale: fr })}
              </DialogTitle>
            </DialogHeader>
            {editingDay && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-500 mb-1 block">Type de journée</Label>
                    <Select
                      value={editingDay.type_journee || 'formation'}
                      onValueChange={(v) => setEditingDay({ ...editingDay, type_journee: v, organisme_externe: v === 'formation' ? '' : editingDay.organisme_externe })}
                    >
                      <SelectTrigger className="h-9 text-sm" data-testid="edit-day-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="formation"><span className="text-indigo-600 font-medium">Formation</span></SelectItem>
                        <SelectItem value="stage"><span className="text-violet-600 font-medium">Stage (PAE)</span></SelectItem>
                        <SelectItem value="prestation_externe"><span className="text-orange-600 font-medium">Prestation externe</span></SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500 mb-1 block">Période</Label>
                    <Select
                      value={editingDay.demi_journee}
                      onValueChange={(v) => setEditingDay({ ...editingDay, demi_journee: v })}
                    >
                      <SelectTrigger className="h-9 text-sm" data-testid="edit-day-period"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="journee">Journée entière <span className="text-slate-400 text-xs">({horairesLabel(horaires, 'journee')})</span></SelectItem>
                        <SelectItem value="matin">Matin <span className="text-slate-400 text-xs">({horairesLabel(horaires, 'matin')})</span></SelectItem>
                        <SelectItem value="apres_midi">Après-midi <span className="text-slate-400 text-xs">({horairesLabel(horaires, 'apres_midi')})</span></SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Collaborateur / Formateur</Label>
                  <Select
                    value={editingDay.formateur_id || 'none'}
                    onValueChange={(v) => setEditingDay({ ...editingDay, formateur_id: v === 'none' ? '' : v })}
                    onOpenChange={(open) => { if (open) fetchDisponibilite(editingDay.date); }}
                  >
                    <SelectTrigger className="h-9 text-sm" data-testid="edit-day-formateur"><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none"><span className="text-slate-400">Non assigné</span></SelectItem>
                      {formateurs.map((f) => {
                        const dispo = getFormateurDispo(f.id, editingDay.date, editingDay.demi_journee);
                        return (
                          <SelectItem key={f.id} value={f.id}>
                            <span className="flex items-center gap-2">
                              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${!dispo ? 'bg-slate-300' : dispo.status === 'available' ? 'bg-emerald-500' : dispo.status === 'multi' ? 'bg-amber-500' : 'bg-slate-400'}`} />
                              <span>{f.nom} {f.prenom}</span>
                              {dispo && dispo.status === 'multi' && <span className="text-[10px] text-amber-600 ml-1">({dispo.label})</span>}
                            </span>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Module</Label>
                  <Select
                    value={editingDay.module_id || 'none'}
                    onValueChange={(v) => setEditingDay({ ...editingDay, module_id: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger className="h-9 text-sm" data-testid="edit-day-module"><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none"><span className="text-slate-400">Non défini</span></SelectItem>
                      {modules.map((m) => (
                        <SelectItem key={m.id} value={m.id}>{m.code} - {m.titre}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Lieu</Label>
                  <div className="flex gap-1">
                    <Select
                      value={editingDay.lieu || ''}
                      onValueChange={(v) => setEditingDay({ ...editingDay, lieu: v })}
                    >
                      <SelectTrigger className="h-9 text-sm flex-1" data-testid="edit-day-lieu"><SelectValue placeholder="Sélectionner un lieu..." /></SelectTrigger>
                      <SelectContent>
                        {lieux.map(l => (
                          <SelectItem key={l.id} value={l.nom}>{l.nom}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button type="button" variant="outline" size="icon" className="h-9 w-9 shrink-0"
                      onClick={() => { setLieuDialogTarget(editingDay.date); setLieuDialogOpen(true); }}>
                      <Plus className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>

                {(editingDay.type_journee === 'stage' || editingDay.type_journee === 'prestation_externe') && (
                  <div>
                    <Label className="text-xs text-slate-500 mb-1 block">
                      {editingDay.type_journee === 'stage' ? 'Lieu / Entreprise du stage' : 'Organisme externe'}
                    </Label>
                    <Input
                      value={editingDay.organisme_externe || ''}
                      onChange={(e) => setEditingDay({ ...editingDay, organisme_externe: e.target.value })}
                      placeholder={editingDay.type_journee === 'stage' ? 'Nom de l\'entreprise...' : 'Nom de l\'organisme...'}
                      className="h-9 text-sm"
                      data-testid="edit-day-organisme"
                    />
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingDay(null)}>Annuler</Button>
              <Button
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="save-day-btn"
                onClick={() => {
                  const newPlanning = formData.planning.map(p =>
                    p.date === editingDay.date ? { ...editingDay } : p
                  );
                  setFormData({ ...formData, planning: newPlanning });
                  setEditingDay(null);
                  toast.success('Séance mise à jour');
                }}
              >
                <Check className="w-4 h-4 mr-2" />Enregistrer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Date Range Selection Dialog */}
        <Dialog open={rangeDialogOpen} onOpenChange={setRangeDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-['Plus_Jakarta_Sans'] flex items-center gap-2">
                <CalendarRange className="w-5 h-5 text-indigo-600" />
                Sélectionner une période
              </DialogTitle>
              <DialogDescription>
                Choisissez une date de début et de fin pour ajouter plusieurs jours en une fois
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date de début</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !rangeStart && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {rangeStart ? format(rangeStart, 'dd/MM/yyyy') : "Sélectionner"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={rangeStart}
                        onSelect={setRangeStart}
                        locale={fr}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label>Date de fin</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !rangeEnd && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {rangeEnd ? format(rangeEnd, 'dd/MM/yyyy') : "Sélectionner"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={rangeEnd}
                        onSelect={setRangeEnd}
                        locale={fr}
                        initialFocus
                        disabled={(date) => rangeStart && date < rangeStart}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              <div className="flex items-center space-x-2 p-3 bg-slate-50 rounded-lg">
                <Checkbox
                  id="excludeWeekends"
                  checked={excludeWeekends}
                  onCheckedChange={setExcludeWeekends}
                />
                <label
                  htmlFor="excludeWeekends"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Exclure les week-ends (samedi et dimanche)
                </label>
              </div>

              {rangeStart && rangeEnd && (
                <div className="p-3 bg-indigo-50 rounded-lg text-sm text-indigo-700">
                  <strong>Aperçu :</strong> Du {format(rangeStart, 'dd MMMM yyyy', { locale: fr })} au {format(rangeEnd, 'dd MMMM yyyy', { locale: fr })}
                  {excludeWeekends && " (jours ouvrés uniquement)"}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setRangeDialogOpen(false)}>
                Annuler
              </Button>
              <Button 
                type="button" 
                className="bg-indigo-600 hover:bg-indigo-700"
                onClick={handleAddDateRange}
                disabled={!rangeStart || !rangeEnd}
              >
                <Plus className="w-4 h-4 mr-2" />
                Ajouter les dates
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};


// Custom scroll container with always-visible scrollbar
